/*
 * I9-SYSTEM: Industry Nine System wheel integration for the LoamLabs Custom Wheel Builder.
 *
 * Plain JavaScript, no libraries, no module syntax. The only global is window.I9System.
 * Loaded after variant-builder.js and spoke-studio.js. variant-builder.js keeps everything block-scoped, so this file
 * reads builder state and caches through window.__loamBuilderInternals (the bridge at the end of variant-builder.js)
 * and is called from small hooks there, each marked "// I9-SYSTEM:". When the build is not a System build every
 * entry point here is a no-op and the classic builder behaves exactly as before.
 *
 * Owns: the Step 1 "Spoke Interface" row, the Step 2 "Hub" row (Solix / Hydra2) and its locks, product eligibility
 * (System vs classic), the Step 3 System spokes UI (one product, color dropdown, wheel previews, selected lines, fee),
 * the Spoke Color Studio launcher (Agent A's window.LoamStudio), the base-color popup, the per-color cart lines and
 * readable order properties, and the Studio-state rehydration.
 */
(function (global) {
  'use strict';

  /* ------------------------------------------------------------------ configuration (edit here) */
  var CFG = {
    systemTag: 'i9_system',
    vendorName: 'Industry Nine',                       // every System rim and hub displays under this vendor
    // Which rim products are DOWNLOADED for a System build (the Storefront search cannot filter by metafield): the ones tagged rimTag (the same tag that puts a rim in
    // the SYSTEM10 discount collection, so approving a rim = this tag + the variant tick), plus every rim of the vendors in rimVendors (We Are One and Industry Nine rims
    // may not carry the tag). A rim is only OFFERED when it also has custom.i9_system_allowed = true and is not single wall (both locks stay). A new brand needs no theme edit.
    rimTag: 'system-discount',
    // ERD sanity check (the FIRST rule for a System rim): a rim is hidden unless it has custom.rim_erd (the variant's, else the product's) and the ERD is inside the range for its
    // wheel size. NOT the spoke calculator: it only catches a missing or mistyped ERD. Ranges = the ERDs of the approved and held rims of that size and the I9 chart rows, plus a
    // 10 mm margin each side (650b shares the 27.5" bead seat 584 and 700c the 29" bead seat 622, so they share a range). Edges and the rims that set them: see
    // agent-prompts/AGENT_B_OTHER_BRAND_RIMS_REPORT.md ("ERD range table"). A size not in this table is hidden.
    rimErdRange: { '26"': [526, 549], '27.5"': [548, 574], '650b': [548, 574], '29"': [586, 614], '700c': [586, 614], '32"': [640, 665] },
    rimVendors: ['We Are One', 'Industry Nine'],
    // Rims of these vendors display under CFG.vendorName ("Industry Nine"); a rim of any other vendor shows its own name.
    relabelVendors: ['We Are One', 'Industry Nine'],
    // true: a rim variant must have the variant metafield custom.i9_system_allowed = true (missing or false = hidden), and its product must not
    // have custom.rim_wall = single. false: every rim variant that passes the vendor and 2:1 checks shows in System.
    // Testing only, on non-published themes: the URL parameter ?i9gate=1 turns it on for that page view, ?i9gate=0 off (see requireRimAllowedFlag()). Nothing is stored.
    requireRimAllowedFlag: true,
    hubModels: {
      Hydra2: { label: 'Hydra2', brake: '6-Bolt',     ratio: '1:1', counts: { front: ['28h', '32h'], rear: ['32h'] }, match: /hydra/i },
      Solix:  { label: 'Solix',  brake: 'Centerlock', ratio: '2:1', counts: { front: ['24h'],        rear: ['24h'] }, match: /solix/i }
    },
    fee: { variantGid: 'gid://shopify/ProductVariant/67791816196403', name: 'Custom Spoke Color Configuration', fallbackCents: 2500 },
    // The fee explanation the Studio's info popup shows (config.feeInfo). The row ORDER is the popup's order: the no-fee row first, then the fee row,
    // whose amount is the fee variant's price (fee: true); a row without fee is $0.
    feeInfo: {
      title: 'Custom spoke color fee',
      note: 'The fee is charged once per wheel.',
      idle: 'No color fee yet',          // the fee box text before any fee applies
      rows: [
        { label: 'One color on a wheel', when: 'No fee.', fee: false },
        { label: 'Two or more colors on a wheel', when: 'Any mix of colors.', fee: true }
      ]
    },
    // ONE name-to-hex list for spoke and hub colors. A color that exists in Shopify but not here still works (gray swatch + name).
    colorHex: {
      Black: '#1d1f23', Blue: '#1f62d6', Bronze: '#8d6a3e', Gold: '#dcae2a', Green: '#2f9e48', Lime: '#a5d12b',
      Orange: '#f07a1a', Pink: '#ee5ba3', Purple: '#7b3fb9', Red: '#d0262b', Silver: '#c3c8cf', Turquoise: '#20b2aa'
    },
    defaultBase: 'Black',
    defaultAccent: 'Orange',
    allFreehubs: ['HG', 'HG-R', 'MS', 'XD', 'XD-R', 'Campy 11/12s', 'Campy N3W'], // the Step 2 freehub buttons; used only when a System hub has no custom.freehub list
    // Industry Nine's own hub builder (Jerry, 2026-10-28): the rear drivers offered per rear spacing, same for Solix and Hydra2. Keyed by "WxL" of the
    // spacing. A driver is offered only when the rule lists it AND the hub variant has a freehub component for it (custom.freehub_variant_map), so a
    // driver without a component product (MS-R today) stays hidden. Spacings not listed here (10x135, 10x141, 12x150) keep the variants' own lists.
    // A driver the variant data lists but the rule does not (for example MS at 12x142) is not offered, except SS (Hydra2 single speed) which stays data-driven.
    freehubRules: {
      '12x148': ['XD', 'HG', 'MS'],
      '12x157': ['XD', 'HG', 'MS'],
      '12x142': ['XD-R', 'HG-R', 'MS-R', 'Campy N3W'],
      '12x135': ['XD-R', 'HG-R', 'MS-R']
    },
    previewSize: 240,
    // Rotate-button images for the Studio's pattern tiles (Agent A's config.arrowImages; ignored by a Studio build without it)
    arrowImages: {
      cw: 'https://cdn.shopify.com/s/files/1/0801/1530/2707/files/clockwise_arrow.png?v=1757300307',
      ccw: 'https://cdn.shopify.com/s/files/1/0801/1530/2707/files/counter-clockwise_arrow.png?v=1757300275'
    },
    // I9-SYSTEM: TODO real data. Studio sample geometry (spec section 3) used until hub flange / crossing metafields exist.
    sample: {
      Solix:  { erd: 568, rear: { drive: { pcd: 52, cross: 3.5 }, rotor: { pcd: 40, cross: 1.4 } }, front: { drive: { pcd: 40, cross: 1.4 }, rotor: { pcd: 43, cross: 3.5 } } },
      Hydra2: { erd: 590, rear: { drive: { pcd: 54, cross: 3 },   rotor: { pcd: 54, cross: 3 } },   front: { drive: { pcd: 46, cross: 2 },   rotor: { pcd: 46, cross: 2 } } }
    },
    text: {
      studioReminder: "You haven't opened the Spoke Color Studio. You can color each spoke individually before checkout.",
      replacedNotice: 'Some saved spoke colors are no longer available and were reset to the base color.',
      resetNotice: 'Your spoke count or hub changed, so the custom spoke colors on the {wheel} wheel were reset to the base color.',
      hubColorUnavailable: '{color} is not available for the {wheel} hub right now, so the hub color was kept.',
      noRims: 'No compatible rims yet.'
    }
  };

  var SIDES = ['drive', 'rotor'];
  var DOT = '·', TIMES = '×';

  /* ------------------------------------------------------------------ bridge helpers */
  function B() { return global.__loamBuilderInternals || null; }
  function bs() { var b = B(); return b ? b.buildState : null; }
  function fn(name) { var b = B(); return b && b.fns && typeof b.fns[name] === 'function' ? b.fns[name] : function () {}; }
  function caches() { var b = B(); return b ? b.caches : { rims: [], hubs: [], spokes: [], nipples: [], valves: [] }; }
  function isSystem() { var s = bs(); return !!(s && s.spokeInterface === 'system'); }
  function model() { var s = bs(); return s && s.specs && s.specs.systemHub && CFG.hubModels[s.specs.systemHub] ? CFG.hubModels[s.specs.systemHub] : null; }
  function modelName() { var m = model(); return m ? m.label : null; }
  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function money(cents) { return fn('formatPrice')(cents); }
  // Only data problems are reported (console.warn); there is no routine logging in production.
  function warn(msg) { if (global.console) global.console.warn('[I9-SYSTEM] ' + msg); }
  function positions() {
    var s = bs(); if (!s || !s.buildType) return [];
    var out = []; if (s.buildType === 'Front' || s.buildType === 'Wheel Set') out.push('front'); if (s.buildType === 'Rear' || s.buildType === 'Wheel Set') out.push('rear'); return out;
  }
  function isTwoToOne(v) { return typeof v === 'string' && /^\s*2\s*:\s*1/.test(v); }
  // Rim variants only: the Spoke Count option value may read "24h 2:1" (a readable label; custom.hub_lacing_ratio stays the source of truth).
  // The hole count is the leading number before "h". Hubs keep plain "24h" values and are never parsed this way.
  function rimHoleCount(value) { var m = String(value == null ? '' : value).match(/^\s*(\d+)\s*h/i); return m ? m[1] + 'h' : null; }
  function rimVariantIsTwoToOne(v) { return isTwoToOne(v.hub_lacing_ratio) || /2\s*:\s*1/.test(String(v.spec_spoke_count || '')); }
  function hasTag(p, tag) { return !!(p && p.tags && p.tags.indexOf(tag) >= 0); }
  function spokeCount(pos) { var s = bs(); var raw = s && s.specs ? s.specs[pos + 'SpokeCount'] : null; var n = parseInt(String(raw || '').replace(/\D/g, ''), 10); return isFinite(n) && n > 0 ? n : 0; }
  function colorOf(variant) { var o = variant && variant.selectedOptions ? variant.selectedOptions.find(function (x) { return x.name && x.name.toLowerCase() === 'color'; }) : null; return o ? o.value : null; }
  function lengthOf(variant) { var o = variant && variant.selectedOptions ? variant.selectedOptions.find(function (x) { return x.name && x.name.toLowerCase() === 'length'; }) : null; return o ? o.value : null; }
  function hexFor(name) { return CFG.colorHex[name] || null; }
  function colorEntry(name) { var h = hexFor(name); return h ? { name: name, hex: h } : { name: name }; }
  function ensureSystemState() { var s = bs(); if (!s) return null; if (!s.system || typeof s.system !== 'object') s.system = { v: 1, studioOpened: false, studio: null }; if (s.system.studioOpened === undefined) s.system.studioOpened = false; return s.system; }

  /* ------------------------------------------------------------------ product eligibility (classic and System) */
  function modelFor(product) {
    if (!product) return null;
    var wm = product.wheelModel || '', title = product.title || '';
    var keys = Object.keys(CFG.hubModels);
    for (var i = 0; i < keys.length; i++) { if (CFG.hubModels[keys[i]].match.test(wm)) return keys[i]; }
    for (var j = 0; j < keys.length; j++) { if (CFG.hubModels[keys[j]].match.test(title)) return keys[j]; }
    return null;
  }
  // An Industry Nine alloy rim (the four aluminumrim products of that brand). A System build shows every rim under the vendor "Industry Nine" (carbon We Are One rims
  // included), so this goes by the product's tags, or its title when the component came back from a saved build / the cart without tags.
  function isAlloyRim(rim) {
    if (!rim) return false;
    var tags = rim.tags || [];
    if (tags.indexOf('aluminumrim') >= 0 && tags.indexOf('industrynine') >= 0) return true;
    return /^industry nine\b.*\balloy rim\b/i.test(String(rim.title || ''));
  }
  // SYSTEM10 (10% off, minimum 6 counted items from the smart collections "System Build Components" and "System Alloy Rims"): a System set is 2 rims + 2 hubs +
  // 2 spoke bundles, so it reaches 6 only when both rims are in those collections. The collections go by product TAGS: system-discount, weareone, i9_system, or
  // industrynine + aluminumrim. The estimate follows the tags, not the brand. A rim component without a tags list (a build restored from an old save) falls back to
  // the brand rule (We Are One / Industry Nine). True when every chosen rim qualifies.
  function rimInDiscountCollections(c) {
    var tags = c && c.tags;
    if (Array.isArray(tags) && tags.length) {
      var has = function (t) { return tags.some(function (x) { return String(x).toLowerCase() === t; }); };
      return has('system-discount') || has('weareone') || has('i9_system') || (has('industrynine') && has('aluminumrim'));
    }
    if (/^\s*we are one\b/i.test(c && c.title || '')) return true;
    return CFG.relabelVendors.indexOf(c && (c.originalVendor || c.vendor)) >= 0;
  }
  function systemDiscountApplies() {
    var s = bs(); if (!s || !isSystem()) return true;
    return ['frontRim', 'rearRim'].every(function (k) {
      var c = s.components && s.components[k]; return !c || rimInDiscountCollections(c);
    });
  }
  // Small notice near the rear rim when the front rim has no variant with the rear wheel's hole count (a 28h-only rim: the Hydra2 rear hub is 32h only), so the rear
  // wheel has a different rim. Information only, nothing is chosen or changed here.
  function optionValue(comp, name) {
    var o = comp && (comp.selectedOptions || []).filter(function (x) { return String(x.name).toLowerCase() === name; })[0];
    return o ? String(o.value) : null;
  }
  function rimPairNotice() {
    var s = bs(); if (!s || !isSystem() || s.buildType !== 'Wheel Set') return null;
    var f = s.components && s.components.frontRim, r = s.components && s.components.rearRim; if (!f || !r || !f.productId || f.productId === r.productId) return null;
    var fh = optionValue(f, 'spoke count'), rh = optionValue(r, 'spoke count'); if (!fh || !rh || fh === rh) return null;
    var fp = (caches().rims || []).filter(function (p) { return p.id === f.productId; })[0], size = optionValue(r, 'size');
    if (!fp || fp.variants.some(function (v) { return rimHoleCount(v.spec_spoke_count) === rimHoleCount(rh) && (!size || (v.selectedOptions || []).some(function (o) { return String(o.value) === size; })); })) return null; // the front rim does come in the rear's hole count
    return 'Your front rim only comes in ' + fh.replace(/h$/i, '') + ' hole and the rear hub is ' + rh.replace(/h$/i, '') + ' hole, so the rear wheel has a different rim: ' + (r.title || 'another rim') + '. You can change it.';
  }
  function syncRimPairNotice() {
    var msg = null; try { msg = rimPairNotice(); } catch (e) { msg = null; }
    var host = document.getElementById('rear-rim-subsection'), el = document.getElementById('i9-rim-pair-notice');
    if (msg && host) {
      if (!el) { el = document.createElement('p'); el.id = 'i9-rim-pair-notice'; el.setAttribute('role', 'status'); el.style.cssText = 'margin:0 0 12px;padding:8px 12px;border-radius:6px;background:#eef4ff;border:1px solid #b9cdf2;font-size:1.3rem;line-height:1.4'; host.insertBefore(el, host.firstChild); }
      el.textContent = msg; el.style.display = '';
    } else if (el) el.style.display = 'none';
    return msg;
  }
  function isSystemHubProduct(p) { return hasTag(p, CFG.systemTag) && !!modelFor(p); }
  function isSystemSpokeProduct(p) { return hasTag(p, CFG.systemTag); }

  // The product caches are filtered for one context: classic, or System with one hub model. A build from another context loaded
  // in the same session (Saved Builds, a share link, a hub-model or interface switch) must not reuse them, or its rims and hubs
  // are "not found" and dropped by the Step 3 revalidation. Each cache remembers the context it was filtered for.
  var cacheKeys = {};
  function filterKey() { return isSystem() ? 'system:' + (modelName() || '') : 'standard'; }
  function cachesStale() {
    var k = filterKey(), c = caches();
    return ['rims', 'hubs', 'spokes'].some(function (kind) { return c[kind] && c[kind].length > 0 && cacheKeys[kind] !== undefined && cacheKeys[kind] !== k; });
  }
  // Called by showStep (steps 2 and 3). Classic-only sessions never have a stale cache, so this is a no-op for them.
  function ensureFreshCaches() {
    if (!cachesStale()) return false;
    if (isSystem()) refillCaches(); else { fn('i9ResetProductCaches')(); fn('triggerEarlyComponentFetches')(); }
    return true;
  }

  /* ------------------------------------------------------------------ System prefetch (both hub models, once per session) */
  // As soon as the build is I9 System (click, Resume, edit-from-cart, share link) the System rims, hubs and spokes are fetched in the
  // background with i9_system-only queries (a handful of products instead of the 215-product classic hub fetch). The raw result
  // holds BOTH hub models; refillCaches() filters it for the chosen model and hands it to the classic caches, so choosing or
  // switching a hub does not wait on the network. The Standard caches are never fed from here (filterEligible keys them apart).
  var PF = { data: null, promise: null, failed: false, startedAt: 0, loadedAt: 0 };
  // fetchBuilderComponentProducts wraps its argument as tag:'…', so these read as full Storefront search queries once wrapped.
  // Rims qualify by vendor (only one of them carries the i9_system tag); hubs and spokes by the tag.
  var PREFETCH_QUERIES = {
    rims: "component:rim' AND (tag:'" + CFG.rimTag + "'" + CFG.rimVendors.map(function (v) { return " OR vendor:'" + v.replace(/'/g, "\\'") + "'"; }).join('') + ") AND tag:'component:rim",
    hubs: CFG.systemTag + "' AND tag:'component:hub",
    spokes: CFG.systemTag + "' AND tag:'component:spoke"
  };
  function isPrefetchTag(tag) { return tag === PREFETCH_QUERIES.rims || tag === PREFETCH_QUERIES.hubs || tag === PREFETCH_QUERIES.spokes; }
  function prefetch() {
    if (PF.data) return Promise.resolve(PF.data);
    if (PF.promise) return PF.promise;
    if (PF.failed) return null; // one attempt per session; the on-demand path takes over
    var b = B(); var fetchFn = b && b.fns && b.fns.fetchBuilderComponentProducts; if (typeof fetchFn !== 'function') return null;
    PF.startedAt = Date.now();
    PF.promise = Promise.all([PREFETCH_QUERIES.rims, PREFETCH_QUERIES.hubs, PREFETCH_QUERIES.spokes].map(function (t) { return fetchFn(t); }))
      .then(function (res) {
        PF.data = { rims: res[0] || [], hubs: res[1] || [], spokes: res[2] || [] }; PF.promise = null; PF.loadedAt = Date.now();
        // Late by definition: whatever the user chose meanwhile is the context the data is filtered for (older choices are ignored).
        if (applyPrefetched()) { var s = bs(); if (s && s.step === 2) rerenderStep2(); else if (s && s.step === 3) renderQuickAdjusters(); }
        return PF.data;
      })
      .catch(function (e) {
        PF.promise = null; PF.failed = true; warn('System prefetch failed (' + (e && e.message) + '); loading on demand');
        if (isSystem() && !caches().hubs.length) fn('triggerEarlyComponentFetches')();
        return null;
      });
    return PF.promise;
  }
  // Classic caches <- prefetched products filtered for the current context (filterEligible: model, ratio, vendors; sets the cache keys).
  function applyPrefetched() {
    if (!PF.data || !isSystem()) return false;
    checkRimGate(PF.data.rims);
    fn('i9SetProductCaches')({ rims: filterEligible('component:rim', PF.data.rims), hubs: filterEligible('component:hub', PF.data.hubs), spokes: filterEligible('component:spoke', PF.data.spokes) });
    return true;
  }
  // Called whenever the System context changes (interface switch, hub model, a build restored from another context).
  function refillCaches() {
    fn('i9ResetProductCaches')(); // also discards any fetch still in flight for the previous context
    if (applyPrefetched()) return true;
    if (!prefetch()) fn('triggerEarlyComponentFetches')(); // no prefetch possible (failed earlier): classic on-demand loading
    return false; // the Step 2 rows stay in their tiered "Loading…" state until the prefetch lands
  }
  function rerenderStep2() { fn('renderStep2')(); fn('updateStep2OptionsVisibility')(); fn('updateStep2VisualFeedback')(); fn('updateSpecAvailabilityCounters')(); fn('updateFreehubLockUI')(); fn('validateStep2')(); }
  // True while no product data has arrived yet for this context (prefetch or classic fetch still in flight). An empty cache after
  // the data landed is a real answer ("no rims for this hub"), not a loading state. A classic fetch is watched once so the rows
  // re-render when it settles.
  var settledFetches = typeof WeakSet === 'function' ? new WeakSet() : null, watchedFetches = settledFetches ? new WeakSet() : null;
  function dataPending(kind) {
    if (caches()[kind].length || PF.data) return false;
    if (PF.promise) return true;
    var b = B(), p = b && b.componentFetchPromises && b.componentFetchPromises[kind];
    if (!p || !settledFetches || settledFetches.has(p)) return false;
    if (!watchedFetches.has(p)) { watchedFetches.add(p); var done = function () { settledFetches.add(p); var s = bs(); if (s && s.step === 2 && isSystem()) rerenderStep2(); }; p.then(done, done); }
    return true;
  }

  // Rim variants for System: vendor in the list and the variant ratio matches the chosen model (unknown model: either ratio).
  // The published theme (Shopify.theme.role "main") never takes the URL override: a customer cannot switch the gate off with ?i9gate=0.
  // Preview, development and unpublished themes still do (testing only). Unknown role counts as published.
  function themeIsPublished() {
    var t = global.Shopify && global.Shopify.theme;
    return !t || !t.role || t.role === 'main';
  }
  function requireRimAllowedFlag() {
    var m = themeIsPublished() ? null : /[?&]i9gate=([01])(?:&|#|$)/.exec(global.location ? global.location.search : '');
    return m ? m[1] === '1' : CFG.requireRimAllowedFlag;
  }
  // Single-wall rims never take System spokes: product metafield custom.rim_wall = "single" hides the rim whatever the approval flag says (blank = not hidden).
  function rimIsSingleWall(p) { return String(p && p.rim_wall || '').trim().toLowerCase() === 'single'; }
  // Mirrors the download query: a rim product tagged CFG.rimTag, or from a vendor in CFG.rimVendors.
  function rimIsDownloadable(p) {
    var tags = (p && p.tags) || [];
    if (tags.some(function (t) { return String(t).toLowerCase() === CFG.rimTag; })) return true;
    return CFG.rimVendors.indexOf(p && (p.originalVendor || p.vendor)) >= 0;
  }
  // The wheel size(s) of a rim variant (its wheel_spec_rim_size value, a string or a list).
  function rimSizesOf(v) { var sv = v && v.spec_rim_size && v.spec_rim_size.parsedValue; return sv == null ? [] : (Array.isArray(sv) ? sv : [sv]); }
  var erdWarned = {};
  function erdWarn(p, v, why) { var k = (v && v.id) || ''; if (erdWarned[k]) return; erdWarned[k] = 1; if (String(v && v.i9_system_allowed).toLowerCase() !== 'true') return; /* only a ticked (approved) rim that fails the check is worth a console line */ warn('rim hidden by the ERD check (' + why + '): ' + (p && p.title) + ' / ' + (v && v.title)); }
  // true when the variant has a usable ERD that is plausible for its wheel size (a rim with several sizes must fit every listed size's range)
  function rimErdPlausible(p, v) {
    var raw = v && v.rim_erd != null && String(v.rim_erd).trim() !== '' ? v.rim_erd : (p && p.rim_erd);
    var erd = parseFloat(raw);
    if (!isFinite(erd) || erd <= 0) { erdWarn(p, v, 'no rim_erd'); return false; }
    var sizes = rimSizesOf(v);
    if (!sizes.length) { erdWarn(p, v, 'no wheel size'); return false; }
    for (var i = 0; i < sizes.length; i++) {
      var r = CFG.rimErdRange[sizes[i]];
      if (!r) { erdWarn(p, v, 'no ERD range for size ' + sizes[i]); return false; }
      if (erd < r[0] || erd > r[1]) { erdWarn(p, v, 'ERD ' + erd + ' is outside ' + r[0] + '-' + r[1] + ' for ' + sizes[i]); return false; }
    }
    return true;
  }
  function rimVariantOkForSystem(p, v) {
    if (!rimErdPlausible(p, v)) return false; // ERD sanity check first (see CFG.rimErdRange)
    if (!rimIsDownloadable(p)) return false;
    if (rimIsSingleWall(p)) return false;
    if (requireRimAllowedFlag() && String(v.i9_system_allowed).toLowerCase() !== 'true') return false; // gate: same place as the 2:1 check, so the i9_system-tagged Hush rim is gated too
    var m = model(); if (!m) return true;
    // Solix (2:1): the metafield is the source of truth. Hydra2 (1:1): neither the metafield nor the "2:1" label may be present.
    return m.ratio === '2:1' ? isTwoToOne(v.hub_lacing_ratio) : !rimVariantIsTwoToOne(v);
  }
  // All-blank guard: with the gate on, if the fetched rims carry no flag at all (not even "false"), the metafield is not readable
  // (Storefront access lost, definition removed). Every rim would be hidden silently, so say so on the page and in the console.
  var rimGateBroken = false;
  function checkRimGate(rims) {
    var n = 0, flagged = 0;
    if (requireRimAllowedFlag() && Array.isArray(rims)) rims.forEach(function (p) { (p.variants || []).forEach(function (v) { n++; if (v.i9_system_allowed != null && String(v.i9_system_allowed).trim() !== '') flagged++; }); });
    rimGateBroken = n > 0 && flagged === 0;
    if (rimGateBroken) warn('System rim gate is on but no rim variant has a custom.i9_system_allowed value: the metafield is not readable from the Storefront API. System rims are hidden.');
    var host = document.getElementById('variant-builder-container'), el = document.getElementById('i9-rim-gate-notice');
    if (rimGateBroken && host) {
      if (!el) { el = document.createElement('div'); el.id = 'i9-rim-gate-notice'; el.setAttribute('role', 'alert'); el.style.cssText = 'margin:0 0 16px;padding:12px 16px;border-radius:6px;background:#fff6e5;border:1px solid #f0c36d;font-size:1.4rem;line-height:1.5'; host.insertBefore(el, host.firstChild); }
      el.textContent = 'System rims are temporarily unavailable, please contact us.'; el.style.display = '';
    } else if (el) el.style.display = 'none';
    return rimGateBroken;
  }
  function filterEligible(tag, products) {
    if (!Array.isArray(products)) return products;
    if (isPrefetchTag(tag)) return products; // prefetch: raw System products for both hub models; applyPrefetched filters them later
    var t = String(tag || '');
    if (t.indexOf('component:rim') >= 0 && isSystem()) checkRimGate(products); // classic on-demand path (the prefetch path checks in applyPrefetched)
    var isRims = t.indexOf('component:rim') >= 0, isHubs = t.indexOf('component:hub') >= 0, isSpokes = t.indexOf('component:spoke') >= 0, isNipples = t.indexOf('component:nipple') >= 0;
    if (!isRims && !isHubs && !isSpokes && !isNipples) return products;
    var system = isSystem();
    if (isRims) cacheKeys.rims = filterKey(); else if (isHubs) cacheKeys.hubs = filterKey(); else if (isSpokes) cacheKeys.spokes = filterKey();
    if (isRims) {
      var out = [];
      products.forEach(function (p) {
        if (!system && hasTag(p, CFG.systemTag)) return; // a rim tagged i9_system is System-only, whatever its variants
        // Classic: drop every 2:1 rim variant (metafield, or the "2:1" text as a safety net) so "24h 2:1" never becomes a classic
        // spoke-count button or a classic Step 3 option. System: keep the variants that match the chosen hub model.
        var variants = p.variants.filter(function (v) { return system ? rimVariantOkForSystem(p, v) : !rimVariantIsTwoToOne(v); });
        if (!variants.length) return;
        if (system) {
          // The builder compares counts as "24h", so a "24h 2:1" label is normalized to its hole count; the original stays in spec_spoke_count_raw.
          variants = variants.map(function (v) {
            var holes = rimHoleCount(v.spec_spoke_count);
            if (!holes || holes === String(v.spec_spoke_count)) return v;
            return Object.assign({}, v, { spec_spoke_count: holes, spec_spoke_count_raw: v.spec_spoke_count });
          });
        }
        var copy = Object.assign({}, p, { variants: variants });
        if (system) { // We Are One and Industry Nine rims display under Industry Nine (the hub products supply that vendor's logo); any other brand keeps its own name and logo
          copy.originalVendor = p.vendor;
          if (CFG.relabelVendors.indexOf(p.vendor) >= 0) { copy.vendor = CFG.vendorName; copy.vendorLogoUrl = null; }
        }
        out.push(copy);
      });
      return out;
    }
    if (isHubs) {
      if (!system) return products.filter(function (p) { return !hasTag(p, CFG.systemTag); });
      return products.filter(function (p) { return isSystemHubProduct(p) && (!modelName() || modelFor(p) === modelName()); }).map(function (p) {
        // System hubs keep their freehubs per variant (custom.freehub / custom.freehub_variant_map on the variant). The classic code
        // still reads the product-level list in a few places, so give the product the union of its variants' lists when it has none.
        if (p.availableFreehubs && p.availableFreehubs.length) return p;
        var isRear = p.variants.some(function (v) { return v.spec_position === 'Rear'; });
        if (!isRear) return p;
        var union = unionFreehubs(p.variants);
        if (union.length) return Object.assign({}, p, { availableFreehubs: union, i9FreehubUnion: true });
        // Neither level has data: treat the hub as compatible with every freehub the builder offers and say so.
        warn('hub "' + p.title + '" has no custom.freehub list on its variants or the product; treating it as compatible with every freehub (no freehub component line will be added)');
        return Object.assign({}, p, { availableFreehubs: CFG.allFreehubs.slice(), i9FreehubFallback: true });
      });
    }
    if (isSpokes) return products.filter(function (p) { return system ? isSystemSpokeProduct(p) : !hasTag(p, CFG.systemTag); });
    return products.filter(function (p) { return !hasTag(p, CFG.systemTag); }); // nipples (System has none)
  }

  /* ------------------------------------------------------------------ freehubs (System hubs: per variant, product level as fallback) */
  var freehubWarned = {};
  function unionFreehubs(variants) {
    var seen = {}, out = [];
    (variants || []).forEach(function (v) { (v.variantFreehubs || []).forEach(function (f) { if (f && !seen[f]) { seen[f] = 1; out.push(f); } }); });
    return out;
  }
  function productOfVariant(v, product) {
    if (product) return product;
    if (!v) return null;
    return caches().hubs.find(function (p) { return (v.productId && p.id === v.productId) || p.variants.some(function (x) { return x.id === (v.id || v.variantId); }); }) || null;
  }
  // The freehub codes a hub variant accepts: its own custom.freehub list, else the product's list (classic behavior), logged once per product.
  function dataFreehubListOf(v, product) {
    if (v && v.variantFreehubs && v.variantFreehubs.length) return v.variantFreehubs;
    var p = productOfVariant(v, product);
    var list = p && p.availableFreehubs ? p.availableFreehubs : [];
    if (p && !freehubWarned[p.id]) { freehubWarned[p.id] = 1; warn('hub "' + p.title + '": variant "' + (v && v.title) + '" has no custom.freehub; using the product-level list (' + (list.length ? list.join(', ') : 'empty') + ')'); }
    return list;
  }
  function freehubRuleKey(spacing) { var m = /(\d+)\s*x\s*(\d+)/i.exec(String(spacing || '')); return m ? m[1] + 'x' + m[2] : ''; }
  // The rule's drivers for a rear spacing, or null when the rule says nothing about it.
  function freehubRuleFor(spacing) { var r = CFG.freehubRules[freehubRuleKey(spacing)]; return r ? r.slice() : null; }
  // The freehub codes a rear hub variant offers: the Industry Nine rule for its spacing (limited to drivers that have a component), else its data list.
  function freehubListOf(v, product) {
    var data = dataFreehubListOf(v, product);
    var rule = v && (v.spec_position === 'Rear' || v.spec_position === 'Front/Rear') ? freehubRuleFor(v.spec_hub_spacing) : null;
    if (!rule) return data;
    var map = freehubMapOf(v, product), mapped = Object.keys(map || {});
    if (!mapped.length) return data; // no freehub components known for this variant: nothing to check the rule against
    var out = rule.filter(function (f) { return mapped.indexOf(f) >= 0; });
    if (data.indexOf('SS') >= 0 && out.indexOf('SS') < 0) out.push('SS');
    return out;
  }
  function freehubMapOf(v, product) {
    if (v && v.variantFreehubMap && Object.keys(v.variantFreehubMap).length) return v.variantFreehubMap;
    var p = productOfVariant(v, product);
    if (p && !freehubWarned['map:' + p.id]) { freehubWarned['map:' + p.id] = 1; warn('hub "' + p.title + '": variant "' + (v && v.title) + '" has no custom.freehub_variant_map; using the product-level map'); }
    return p && p.freehubVariantMap ? p.freehubVariantMap : {};
  }
  // Called by setComponentFreehubForHub: the hub object it receives is a variant spread with product fields (product map wins there),
  // or a saved component; either way the variant's own map must be used when it exists.
  function withFreehubMap(hubData) {
    if (!hubData) return hubData;
    var p = productOfVariant(hubData, null);
    var v = p ? p.variants.find(function (x) { return x.id === (hubData.variantId || hubData.id); }) : null;
    if (!v) return hubData;
    return Object.assign({}, hubData, { freehubVariantMap: freehubMapOf(v, p) });
  }
  // Variant-level check used by the Step 3 hub list and the revalidation: Step 2 choices (count, spacing, brake) and, for rear hubs, the freehub.
  function hubVariantAllowed(v, position, product) {
    var s = bs(); if (!s || !isSystem() || !v) return true;
    var pos = String(position || '').toLowerCase(); if (pos !== 'front' && pos !== 'rear') return true;
    var norm = fn('normalizeSpacing');
    var count = s.specs[pos + 'SpokeCount'], spacing = s.specs[pos + 'HubSpacing'], brake = s.specs.brakeStyle;
    if (count && String(v.spec_spoke_count) !== String(count)) return false;
    if (spacing && norm(v.spec_hub_spacing) !== norm(spacing)) return false;
    if (brake && v.spec_brake_interface && v.spec_brake_interface.length && v.spec_brake_interface.indexOf(brake) < 0) return false;
    if (pos === 'rear' && s.specs.freehubType && !fn('isFreehubCompatible')(s.specs.freehubType, freehubListOf(v, product))) return false;
    return true;
  }
  // Union of the freehub codes listed by every rear variant of the chosen model at the given spacing (any color or count). No spacing: every rear variant.
  function availableFreehubsForSpacing(spacing) {
    var norm = fn('normalizeSpacing'), seen = {}, out = [];
    systemHubProducts('rear').forEach(function (p) {
      p.variants.forEach(function (v) {
        if (v.spec_position !== 'Rear' && v.spec_position !== 'Front/Rear') return;
        if (!v.availableForSale) return;
        if (spacing && norm(v.spec_hub_spacing) !== norm(spacing)) return;
        freehubListOf(v, p).forEach(function (f) { if (f && !seen[f]) { seen[f] = 1; out.push(f); } });
      });
    });
    return out;
  }
  // Step 2 Freehub row for System builds: buttons follow the live variant data; SS appears only when some variant lists it.
  function syncFreehubButtons() {
    var s = bs(); var row = document.querySelector('#step-2 .spec-group[data-spec-name="freehubType"]'); if (!s || !row) return;
    var system = isSystem(); var buttons = row.querySelectorAll('button[data-spec-group="freehub"]');
    var ss = row.querySelector('button[data-value="SS"]');
    var msr = row.querySelector('button[data-value="MS-R"]'); // shown only when a System hub variant offers it (needs its freehub component product)
    if (!system) {
      if (ss) ss.style.display = 'none';
      if (msr) msr.style.display = 'none';
      buttons.forEach(function (b) { if (b.dataset.i9Disabled) { b.disabled = false; delete b.dataset.i9Disabled; } });
      return;
    }
    var hasSpacing = !!s.specs.rearHubSpacing;
    var forSpacing = availableFreehubsForSpacing(s.specs.rearHubSpacing), anySpacing = availableFreehubsForSpacing(null);
    if (ss) ss.style.display = anySpacing.indexOf('SS') >= 0 ? '' : 'none';
    if (msr) msr.style.display = anySpacing.indexOf('MS-R') >= 0 ? '' : 'none';
    var dataReady = caches().hubs.length > 0;
    buttons.forEach(function (b) {
      var ok = !dataReady || forSpacing.indexOf(b.dataset.value) >= 0;
      b.classList.toggle('is-unavailable', !ok);
      b.disabled = !ok; if (!ok) b.dataset.i9Disabled = '1'; else delete b.dataset.i9Disabled;
      b.title = ok ? '' : (hasSpacing ? 'Not offered for ' + s.specs.rearHubSpacing + ' on this hub' : 'Not offered on this hub');
    });
    if (dataReady && hasSpacing) {
      var changed = false;
      if (s.specs.freehubType && forSpacing.indexOf(s.specs.freehubType) < 0) { s.specs.freehubType = null; delete s._ui.step2AutoSelected.freehubType; changed = true; }
      // Pre-select a lone freehub only once the classic lock's prerequisites are met (brake, rear size, count, spacing); before that the
      // classic updateFreehubLockUI clears any freehub choice.
      var prereqs = s.specs.brakeStyle && s.specs.rearWheelSize && s.specs.rearSpokeCount && s.specs.rearHubSpacing;
      if (prereqs && !s.specs.freehubType && forSpacing.length === 1) { s.specs.freehubType = forSpacing[0]; s._ui.step2AutoSelected.freehubType = forSpacing[0]; changed = true; }
      if (changed) afterAutoSelect();
    }
  }
  // An automatic selection must leave Step 2 exactly as a manual click would: highlight, counters and badges on every row
  // (the freehub narrows which rear spacings still have a hub), Next button, auto-save.
  var inAutoSelect = false;
  function afterAutoSelect() {
    if (!isSystem()) return; // classic auto-selects keep their baseline behaviour (the hook in updateStep2OptionsVisibility runs for every build)
    if (inAutoSelect) return; // the counters pass re-enters syncFreehubButtons through validateStep2/updateFreehubLockUI
    inAutoSelect = true;
    try {
      fn('updateStep2VisualFeedback')();
      fn('updateSpecAvailabilityCounters')();
      fn('validateStep2')();
      fn('saveLastActiveBuild')();
    } finally { inAutoSelect = false; }
  }

  /* ------------------------------------------------------------------ Step 1: Spoke Interface */
  function hasProgress() {
    var s = bs(); if (!s) return false;
    if (s._ui && s._ui.hasProgressedPastStep1) return true;
    var specsSet = Object.keys(s.specs || {}).some(function (k) { return s.specs[k] !== null && s.specs[k] !== undefined; });
    var compsSet = Object.keys(s.components || {}).some(function (k) { var c = s.components[k]; return c && !(Array.isArray(c) && !c.length); });
    return specsSet || compsSet;
  }
  function confirmDialog(title, bodyHtml, confirmText, cancelText, onConfirm, onCancel) {
    var modal = document.getElementById('spec-change-confirm-modal'), t = document.getElementById('spec-change-modal-title'), b = document.getElementById('spec-change-modal-body');
    var ok = document.getElementById('spec-change-modal-confirm-btn'), no = document.getElementById('spec-change-modal-cancel-btn'), x = document.getElementById('spec-change-modal-close-btn');
    if (!modal || !t || !b || !ok || !no) { if (global.confirm(title)) onConfirm(); else if (onCancel) onCancel(); return; }
    // The modal markup lives inside #step-3 and showStep hides every other step, so from Step 1 or 2 it opened invisibly: the
    // "I9 System" button after Back from Step 2, and a hub switch after Back from Step 3, looked dead. It is shown from any
    // step now, so it moves to body level once; the classic code keeps its references by id and the fixed-position CSS is unchanged.
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
    t.textContent = title; b.innerHTML = bodyHtml;
    ok.style.display = ''; ok.textContent = confirmText; no.textContent = cancelText;
    ok.onclick = function () { modal.style.display = 'none'; onConfirm(); };
    var cancel = function () { modal.style.display = 'none'; if (onCancel) onCancel(); };
    no.onclick = cancel; if (x) x.onclick = cancel;
    modal.style.display = 'flex';
  }
  function resetBuildForInterfaceChange() {
    var s = bs(); if (!s) return;
    Object.keys(s.specs).forEach(function (k) { s.specs[k] = null; });
    Object.keys(s.components).forEach(function (k) { s.components[k] = /Addons$/.test(k) ? [] : null; });
    s.selectedOptionalAddons = {};
    s._ui.step2AutoSelected = {};
    s._ui.hasProgressedPastStep1 = false;
    s.system = null;
    destroyPreviews();
    fn('clearStep3UI')();
    fn('updateBuildSummary')();
  }
  function onSpokeInterfaceClick(value, button) {
    var s = bs(); if (!s) return;
    if (s.spokeInterface === value) return;
    var apply = function (reset) {
      if (reset) resetBuildForInterfaceChange();
      s.spokeInterface = value;
      if (value === 'system') {
        ensureSystemState();
        s.customerSupplied = { front: { rim: false, hub: false }, rear: { rim: false, hub: false } };
        document.querySelectorAll('#customer-supplied-section .customer-supplied-checkbox').forEach(function (cb) { cb.checked = false; });
        var hybridBtn = s.ridingStyle !== 'hybrid' ? document.querySelector('#step-1 button.selection-button[data-selection-type="ridingStyle"][data-value="hybrid"]') : null;
        if (hybridBtn) {
          // The classic Riding Style handler resets specs/components when the style changes and starts the fetches (now in System mode).
          hybridBtn.click();
        } else if (s.ridingStyle !== 'hybrid') { s.ridingStyle = 'hybrid'; s.ridingStyleDisplay = 'Hybrid: Gr/Rd/Mtb'; }
        refillCaches(); // prefetched System products when they are in, else the prefetch starts now (the classic fetch the Hybrid click started is superseded)
      } else {
        s.system = null;
        s.specs.systemHub = null;
        destroyPreviews();
        fn('i9ResetProductCaches')();
        fn('triggerEarlyComponentFetches')();
      }
      fn('updateStep1VisualFeedback')();
      fn('validateStep1')();
      fn('updateCustomerSuppliedCheckboxesVisibility')();
      syncStep1UI();
      if (s.step >= 2) { fn('renderStep2')(); fn('updateStep2OptionsVisibility')(); fn('updateStep2VisualFeedback')(); fn('validateStep2')(); }
      fn('updateBuildSummary')();
      fn('saveLastActiveBuild')();
    };
    // The first choice (nothing was selected yet) needs no confirmation unless it is System and earlier selections (a pre-build link) would be cleared.
    if (hasProgress() && (s.spokeInterface || value === 'system')) {
      confirmDialog('Change spoke interface?',
        '<p>Switching between <strong>Standard</strong> and <strong>I9 System</strong> spokes clears your specs and component selections. Your build type is kept.</p><p>Do you want to continue?</p>',
        'Yes, switch', 'Cancel',
        function () { apply(true); },
        function () { fn('updateStep1VisualFeedback')(); });
    } else {
      apply(false);
    }
  }
  function syncStep1UI() {
    var s = bs(); if (!s) return;
    var system = s.spokeInterface === 'system';
    var row = document.querySelector('#step-1 .selection-row[data-group-type="ridingStyle"]');
    if (row) {
      row.classList.toggle('i9-locked', system);
      // pointer-events:none only stops the mouse; a keyboard user could still Tab to "Mountain" and switch a System build off Hybrid
      row.querySelectorAll('button[data-selection-type="ridingStyle"]').forEach(function (b) { b.disabled = system && b.dataset.value !== 'hybrid'; });
      var note = row.querySelector('.i9-lock-note');
      if (system && !note) { note = document.createElement('span'); note.className = 'i9-lock-note'; note.textContent = 'Set by I9 System'; var label = row.querySelector('.selection-label'); if (label) label.appendChild(note); }
      if (!system && note) note.remove();
    }
    var cs = document.getElementById('customer-supplied-section');
    if (cs) cs.classList.toggle('i9-hidden', system);
    if (system) prefetch(); // a System build restored on load (Resume, edit-from-cart, share link) starts the prefetch here
    document.querySelectorAll('#step-1 button[data-selection-type="spokeInterface"]').forEach(function (b) { b.classList.toggle('active', b.dataset.value === s.spokeInterface); });
  }

  /* ------------------------------------------------------------------ Step 2: Hub row, locks, buttons, counts */
  function onHubModelChange(value) {
    var s = bs(); if (!s || !CFG.hubModels[value]) return;
    if (s.specs.systemHub === value) return;
    var m = CFG.hubModels[value];
    var hadComponents = ['frontRim', 'rearRim', 'frontHub', 'rearHub', 'frontSpokes', 'rearSpokes'].some(function (k) { return !!s.components[k]; });
    var apply = function () {
      s.specs.systemHub = value;
      s.specs.brakeStyle = m.brake;
      // Wheel sizes are cleared too: the eligible rims (2:1 vs standard) change with the model, so a size from the other model can be stale.
      ['frontWheelSize', 'rearWheelSize', 'frontSpokeCount', 'rearSpokeCount', 'frontHubSpacing', 'rearHubSpacing', 'freehubType'].forEach(function (k) { s.specs[k] = null; });
      s._ui.step2AutoSelected = {};
      if (hadComponents) {
        ['frontRim', 'rearRim', 'frontHub', 'rearHub', 'frontSpokes', 'rearSpokes', 'frontNipples', 'rearNipples', 'frontValveStem', 'rearValveStem', 'rearComponentFreehub'].forEach(function (k) { s.components[k] = null; });
        s.selectedOptionalAddons = {};
        destroyPreviews();
        fn('clearStep3UI')();
      }
      if (s.system) { s.system.studio = null; s.system.studioOpened = false; } // a new hub model is a new wheel: the Studio reminder applies again
      refillCaches(); // hub model decides which hubs and which rim variants are eligible: prefetched data when it is in, else on demand
      fn('renderStep2')();
      fn('updateStep2OptionsVisibility')();
      fn('updateStep2VisualFeedback')();
      fn('updateSpecAvailabilityCounters')();
      fn('updateFreehubLockUI')();
      fn('updateBuildSummary')();
      fn('saveLastActiveBuild')();
    };
    if (hadComponents) {
      confirmDialog('Change hub?', '<p>Changing the hub model clears your rim, hub and spoke selections (and any custom spoke colors).</p><p>Continue?</p>', 'Yes, change hub', 'Cancel', apply, function () { fn('updateStep2VisualFeedback')(); });
    } else apply();
  }
  function protectSpecs(healed, current) {
    if (!isSystem()) return;
    healed.systemHub = current.systemHub;
    healed.brakeStyle = current.brakeStyle;
    // A freehub the new rear spacing does not offer must not survive into the healing test set: with it every combination tests
    // invalid and the healing strips every other spec (seen when moving from an SS-only spacing to Boost).
    if (healed.freehubType && current.rearHubSpacing && availableFreehubsForSpacing(current.rearHubSpacing).indexOf(healed.freehubType) < 0) healed.freehubType = null;
  }
  function syncStep2UI() {
    var s = bs(); var step2 = document.getElementById('step-2'); if (!s || !step2) return;
    var system = s.spokeInterface === 'system';
    var hubRow = step2.querySelector('.spec-group[data-spec-name="systemHub"]');
    if (hubRow) hubRow.style.display = system ? 'flex' : 'none';
    var hasModel = !!(system && s.specs.systemHub);
    step2.querySelectorAll('.spec-group.selection-row').forEach(function (row) {
      var name = row.dataset.specName;
      if (name === 'systemHub') return;
      // For System builds the other rows wait for a hub model; the classic build-type visibility already ran.
      if (system && !hasModel && row.style.display !== 'none') row.style.display = 'none';
      if (name === 'brakeStyle') {
        row.classList.toggle('i9-locked', hasModel);
        // keyboard users must not be able to pick the other brake interface under a System hub (the row is only mouse-locked by CSS)
        row.querySelectorAll('button').forEach(function (b) { b.disabled = hasModel && b.dataset.value !== CFG.hubModels[s.specs.systemHub].brake; });
        var note = row.querySelector('.i9-lock-note');
        if (hasModel) {
          if (!note) { note = document.createElement('span'); note.className = 'i9-lock-note'; var label = row.querySelector('.selection-label'); if (label) label.appendChild(note); }
          note.textContent = 'Set by the ' + s.specs.systemHub + ' hub'; // always refreshed: it read "Hydra2" after switching to Solix
        } else if (note) note.remove();
        var rimBrake = row.querySelector('button[data-value="Rim Brake"]');
        if (rimBrake && system) rimBrake.style.display = 'none';
      }
    });
    var hint = step2.querySelector('.i9-step2-hint');
    if (system && !hasModel) {
      if (!hint && hubRow) { hint = document.createElement('p'); hint.className = 'i9-step2-hint'; hint.textContent = 'Choose a hub to see the sizes, spoke counts and spacings it supports.'; hubRow.insertAdjacentElement('afterend', hint); }
    } else if (hint) hint.remove();
    if (system) prefetch();
    tierStep2Rows(step2, s, hasModel);
    syncFreehubButtons();
  }
  // Tiered reveal (System only): after the hub, rows appear strictly top to bottom and only below rows that show their real buttons.
  // A row whose data is still loading is hidden, with everything under it, and one "Loading…" line sits under the last visible row.
  // With the prefetch in, every row is ready at once; this is the safety net for slow connections.
  var TIERS = [['frontWheelSize', 'rearWheelSize'], ['frontSpokeCount', 'rearSpokeCount', 'brakeStyle'], ['frontHubSpacing', 'rearHubSpacing'], ['freehubType']];
  function classicShowsRow(s, name) { // mirrors renderStep2's build-type filter
    var bT = s.buildType;
    if (name === 'freehubType') return bT === 'Rear' || bT === 'Wheel Set';
    if (name.indexOf('front') === 0) return bT === 'Front' || bT === 'Wheel Set';
    if (name.indexOf('rear') === 0) return bT === 'Rear' || bT === 'Wheel Set';
    return true;
  }
  var tierRevealPending = false;
  function tierStep2Rows(step2, s, active) {
    var line = step2.querySelector('.i9-step2-loading');
    if (!active) { if (line) line.remove(); return; }
    var blocked = false, loading = false, revealed = false, last = step2.querySelector('.spec-group[data-spec-name="systemHub"]');
    TIERS.forEach(function (names) {
      var rows = [];
      names.forEach(function (n) { var r = step2.querySelector('.spec-group.selection-row[data-spec-name="' + n + '"]'); if (r && classicShowsRow(s, n)) rows.push(r); });
      if (!blocked && !rows.every(function (r) { var c = r.querySelector('.spec-options'); return !(c && c.dataset.i9Loading); })) { blocked = true; loading = true; }
      rows.forEach(function (r) {
        var show = !blocked;
        if (show && r.style.display === 'none') revealed = true;
        r.style.display = show ? 'flex' : 'none';
        if (show) last = r;
      });
    });
    if (loading) {
      if (!line) { line = document.createElement('p'); line.className = 'i9-step2-loading'; line.setAttribute('role', 'status'); line.textContent = 'Loading…'; }
      if (last && line.previousElementSibling !== last) last.insertAdjacentElement('afterend', line);
    } else if (line) line.remove();
    // Rows revealed after their data landed get the classic visibility pass (single-option auto-select, e.g. Solix 24h) once.
    if (revealed && !tierRevealPending) {
      tierRevealPending = true;
      setTimeout(function () { tierRevealPending = false; fn('updateStep2OptionsVisibility')(); fn('updateStep2VisualFeedback')(); fn('validateStep2')(); }, 0);
    }
  }
  function systemHubProducts(pos) {
    var m = modelName(); if (!m) return [];
    var posPascal = cap(pos);
    return caches().hubs.filter(function (p) {
      if (!isSystemHubProduct(p) || modelFor(p) !== m) return false;
      return p.variants.some(function (v) { return v.spec_position === posPascal || v.spec_position === 'Front/Rear'; });
    });
  }
  function bubble(cls) { return '<span class="spec-count-bubble ' + cls + ' is-loading" title="Matching components available"></span>'; }
  function generateStep2Buttons(position, kind, container) {
    renderStep2Buttons(position, kind, container);
    if (container) { if (container.querySelector('.i9-empty[data-loading]')) container.dataset.i9Loading = '1'; else delete container.dataset.i9Loading; }
    var step2 = document.getElementById('step-2'), s = bs();
    if (step2 && s) { tierStep2Rows(step2, s, isSystem() && !!s.specs.systemHub); if (kind === 'size') markSizePairing(step2); }
  }
  function renderStep2Buttons(position, kind, container) {
    var s = bs(); var m = model(); if (!s || !container) return;
    if (!m) { container.innerHTML = ''; return; }
    var posPascal = cap(position);
    if (kind === 'count') {
      var counts = m.counts[position] || [];
      container.innerHTML = counts.map(function (c) {
        return '<button type="button" class="spec-option-button builder-button ' + (s.specs[position + 'SpokeCount'] === c ? 'active' : '') + '" data-spec-group="' + position + 'SpokeCount" data-value="' + c + '" data-riding-style="both">' + c + '</button>';
      }).join('');
      return;
    }
    if (kind === 'size') {
      var sizes = {};
      caches().rims.forEach(function (p) {
        p.variants.forEach(function (v) {
          if (!v.availableForSale) return;
          if (v.spec_position !== posPascal && v.spec_position !== 'Front/Rear') return;
          var val = v.spec_rim_size && v.spec_rim_size.parsedValue; if (!val) return;
          (Array.isArray(val) ? val : [val]).forEach(function (x) { sizes[x] = true; });
        });
      });
      var order = ['20"', '24"', '26"', '650b', '27.5"', '700c', '29"', '32"'];
      var list = Object.keys(sizes).sort(function (a, b) { var ia = order.indexOf(a), ib = order.indexOf(b); if (ia < 0) ia = 999; if (ib < 0) ib = 999; return ia - ib || a.localeCompare(b); });
      if (!list.length) { container.innerHTML = dataPending('rims') ? '<p class="i9-empty" data-loading="1">Loading rims…</p>' : '<p class="i9-empty">' + CFG.text.noRims + '</p>'; return; }
      container.innerHTML = list.map(function (size) {
        return '<button type="button" class="spec-option-button builder-button ' + (s.specs[position + 'WheelSize'] === size ? 'active' : '') + '" data-spec-group="' + position + 'WheelSize" data-value=\'' + size + '\' data-riding-style="both">' + size + bubble('is-rim') + '</button>';
      }).join('');
      return;
    }
    if (kind === 'spacing') {
      var spacings = {};
      systemHubProducts(position).forEach(function (p) {
        p.variants.forEach(function (v) {
          if (!v.availableForSale) return;
          if (v.spec_position !== posPascal && v.spec_position !== 'Front/Rear') return;
          if (v.spec_hub_spacing) spacings[v.spec_hub_spacing] = true;
        });
      });
      var sp = Object.keys(spacings).sort(function (a, b) { return a.localeCompare(b, undefined, { numeric: true }); });
      if (!sp.length) { container.innerHTML = dataPending('hubs') ? '<p class="i9-empty" data-loading="1">Loading hubs…</p>' : '<p class="i9-empty">No ' + m.label + ' ' + position + ' hub spacings found in the store.</p>'; return; }
      container.innerHTML = sp.map(function (spacing) {
        return '<button type="button" class="spec-option-button builder-button ' + (s.specs[position + 'HubSpacing'] === spacing ? 'active' : '') + '" data-spec-group="' + position + 'HubSpacing" data-value="' + esc(spacing) + '" data-riding-style="both">' + esc(spacing) + bubble('is-hub') + '</button>';
      }).join('');
    }
  }
  /* ------------------------------------------------------------------ wheel-size pairing (System wheel sets) */
  // Front size -> the rear sizes it may be paired with. The same size is always allowed; 29" and 700c mix freely (both 622 mm bead
  // seat); a mullet (29" or 700c front, 27.5" rear) is allowed; reverse mullets (27.5" front with 29"/700c rear) and any mix with
  // 26" or 32" are not. One table, read from both sides; a size missing from the table pairs only with itself.
  var SIZE_PAIRS = { '26"': ['26"'], '27.5"': ['27.5"'], '29"': ['29"', '700c', '27.5"'], '700c': ['700c', '29"', '27.5"'], '32"': ['32"'] };
  function isPairAllowed(front, rear) { if (!front || !rear || front === rear) return true; var list = SIZE_PAIRS[front]; return !!list && list.indexOf(rear) >= 0; }
  function allowedRearsFor(front) { return SIZE_PAIRS[front] ? SIZE_PAIRS[front].slice() : [front]; }
  function allowedFrontsFor(rear) { var out = Object.keys(SIZE_PAIRS).filter(function (f) { return isPairAllowed(f, rear); }); if (out.indexOf(rear) < 0) out.push(rear); return out; }
  function pairOk(pos, size, otherSize) { return pos === 'front' ? isPairAllowed(size, otherSize) : isPairAllowed(otherSize, size); }
  function pairingActive() { var s = bs(); return !!(s && isSystem() && s.buildType === 'Wheel Set'); }
  function otherWheel(pos) { return pos === 'front' ? 'rear' : 'front'; }
  var SIZE_ORDER = ['20"', '24"', '26"', '650b', '27.5"', '700c', '29"', '32"'];
  // Sizes the chosen hub model's eligible rims come in for this wheel (the Step 2 size list), in rim-size order.
  function sizesFor(pos) {
    var posPascal = cap(pos), sizes = {};
    caches().rims.forEach(function (p) { p.variants.forEach(function (v) {
      if (!v.availableForSale) return;
      if (v.spec_position !== posPascal && v.spec_position !== 'Front/Rear') return;
      var val = v.spec_rim_size && v.spec_rim_size.parsedValue; if (!val) return;
      (Array.isArray(val) ? val : [val]).forEach(function (x) { sizes[x] = true; });
    }); });
    return Object.keys(sizes).sort(function (a, b) { var ia = SIZE_ORDER.indexOf(a), ib = SIZE_ORDER.indexOf(b); if (ia < 0) ia = 999; if (ib < 0) ib = 999; return ia - ib || a.localeCompare(b); });
  }
  // Rims exist for this wheel at `size` given the rest of the specs (plus overrides). Data availability, independent of the pairing rule.
  function sizeAvailable(pos, size, overrides) { var s = bs(); if (!s) return false; var t = Object.assign({}, s.specs, overrides || {}); t[pos + 'WheelSize'] = size; return validCounts(t, cap(pos)).rimCount > 0; }
  // Grays (but keeps clickable) the sizes the other wheel's current size does not pair with. Step 2 rows (data-spec-group) and the
  // Step 3 quick row (data-spec-key) share the class; the "unavailable" look comes from i9-system.css.
  function markSizePairing(root) {
    var s = bs(); if (!s) return; root = root || document;
    root.querySelectorAll('button.spec-option-button[data-spec-group$="WheelSize"], button.spec-option-button[data-spec-key$="WheelSize"]').forEach(function (b) {
      var key = b.dataset.specGroup || b.dataset.specKey, pos = key.indexOf('front') === 0 ? 'front' : 'rear', other = s.specs[otherWheel(pos) + 'WheelSize'];
      b.classList.toggle('i9-pair-blocked', pairingActive() && !!other && !pairOk(pos, b.dataset.value, other));
    });
  }
  // The other wheel's new size when `pos` takes `size`: the same size when rims exist for it, else the single compatible size
  // with rims, else null (cleared, the customer picks). Used by the "Change both" confirm on Step 2 and Step 3.
  function planOtherSize(pos, size) {
    var other = otherWheel(pos), overrides = {}; overrides[pos + 'WheelSize'] = size;
    var have = sizesFor(other);
    if (have.indexOf(size) >= 0 && sizeAvailable(other, size, overrides)) return size;
    var fits = (pos === 'front' ? allowedRearsFor(size) : allowedFrontsFor(size)).filter(function (sz) { return have.indexOf(sz) >= 0 && sizeAvailable(other, sz, overrides); });
    return fits.length === 1 ? fits[0] : null;
  }
  function pairBody(pos, size, otherSize, plan) {
    var otherName = otherWheel(pos) + ' wheel';
    if (plan) return '<p>Choosing <strong>' + esc(size) + '</strong> for the ' + pos + ' wheel will also change the ' + otherName + ' from <strong>' + esc(otherSize) + '</strong> to <strong>' + esc(plan) + '</strong>.</p>';
    return '<p>Choosing <strong>' + esc(size) + '</strong> for the ' + pos + ' wheel does not pair with the ' + otherName + '&rsquo;s <strong>' + esc(otherSize) + '</strong>, and no matching size is available for it. The ' + otherName + ' size will be cleared so you can pick a compatible one.</p>';
  }
  // Once one wheel has a size, a single compatible size with rims left for the other wheel is selected for it (the classic
  // single-option auto-select only sees rows with one visible button; pairing-grayed buttons stay visible).
  function pairingAutoSelect() {
    var s = bs(); if (!s || !pairingActive()) return false;
    var changed = false;
    ['front', 'rear'].forEach(function (pos) {
      if (s.specs[pos + 'WheelSize']) return;
      var other = s.specs[otherWheel(pos) + 'WheelSize']; if (!other) return;
      var fits = sizesFor(pos).filter(function (sz) { return pairOk(pos, sz, other) && sizeAvailable(pos, sz); });
      if (fits.length === 1) { s.specs[pos + 'WheelSize'] = fits[0]; if (s._ui && s._ui.step2AutoSelected) s._ui.step2AutoSelected[pos + 'WheelSize'] = fits[0]; changed = true; }
    });
    return changed;
  }
  // Hook from updateMtbWheelSizeCompatibility (runs after every Step 2 click): gray the pairs and auto-select a lone compatible size.
  // The classic flow that follows (visual feedback, counters, freehub lock, save) picks the auto-selection up like any other spec.
  function onSizeCompat(step2) { pairingAutoSelect(); markSizePairing(step2); }
  var pairingBypass = false;
  // Step 2 click hook. A pairing-grayed size asks first; on "Change both" the other wheel moves and the button is re-clicked, so the
  // classic path (healing, counters, tiered rows, badges, prefetched caches) runs exactly as for any other spec change.
  function onSizeClick(key, value, button) {
    if (pairingBypass || !pairingActive() || !/WheelSize$/.test(key)) return false;
    var s = bs(), pos = key.indexOf('front') === 0 ? 'front' : 'rear', other = otherWheel(pos), otherSize = s.specs[other + 'WheelSize'];
    if (!otherSize || pairOk(pos, value, otherSize) || s.specs[key] === value) return false;
    var plan = planOtherSize(pos, value);
    confirmDialog('Change both wheel sizes?', pairBody(pos, value, otherSize, plan), 'Change both', 'Cancel', function () {
      s.specs[other + 'WheelSize'] = plan;
      if (s._ui && s._ui.step2AutoSelected) delete s._ui.step2AutoSelected[other + 'WheelSize'];
      pairingBypass = true;
      try { button.click(); } finally { pairingBypass = false; }
    }, function () { fn('updateStep2VisualFeedback')(); });
    return true;
  }
  // Step 3 quick-row click hook (classic adjuster handler). Brake style is set by the hub; a non-pairing size asks "Change both" and
  // then goes through updateSpecAndRefreshStep3 like a classic quick change (component revalidation, reset notice, Step 2 updated).
  function onAdjusterClick(key, value, button) {
    if (!isSystem()) return false;
    if (!/WheelSize$/.test(key) || !pairingActive()) return false;
    var s = bs(), pos = key.indexOf('front') === 0 ? 'front' : 'rear', other = otherWheel(pos), otherSize = s.specs[other + 'WheelSize'];
    if (!otherSize || pairOk(pos, value, otherSize)) return false;
    var plan = planOtherSize(pos, value);
    confirmDialog('Change both wheel sizes?', pairBody(pos, value, otherSize, plan), 'Change both', 'Cancel', function () {
      var upd = {}; upd[key] = value; upd[other + 'WheelSize'] = plan;
      if (s._ui && s._ui.step2AutoSelected) delete s._ui.step2AutoSelected[other + 'WheelSize'];
      fn('updateSpecAndRefreshStep3')(upd);
    }, null);
    return true;
  }
  // Step 3 quick row for System builds (same markup and classes as the Standard adjusters, so its click handler and CSS apply):
  // Front / Rear cards with Wheel Size and Spoke Count (only options that exist for the chosen hub and rims), a Brake Style card
  // showing the hub-set choice as a non-clickable selected button with a note.
  function renderQuickAdjusters() {
    var s = bs(), m = model();
    var sec = document.getElementById('spec-adjuster-section'), front = document.getElementById('front-spec-adjusters'), rear = document.getElementById('rear-spec-adjusters'), center = document.getElementById('center-spec-adjusters'), wrapper = document.getElementById('spec-adjuster-wrapper');
    if (!s || !sec || !front || !rear || !center || !wrapper) return;
    if (!m || !s.buildType) { sec.style.display = 'none'; return; }
    var bT = s.buildType, showF = bT === 'Front' || bT === 'Wheel Set', showR = bT === 'Rear' || bT === 'Wheel Set';
    var group = function (pos, key, label, options, blockedFn) {
      var cur = s.specs[key];
      return '<div class="quick-spec-group"><label>' + label + '</label><div class="spec-adjuster-options">' + options.map(function (o) {
        return '<button type="button" class="builder-button spec-option-button' + (cur === o ? ' active' : '') + (blockedFn && blockedFn(o) ? ' i9-pair-blocked' : '') + '" data-position="' + cap(pos) + '" data-spec-key="' + key + '" data-value=\'' + esc(o) + '\'>' + esc(o) + '</button>';
      }).join('') + '</div></div>';
    };
    var column = function (pos) {
      var sizes = sizesFor(pos), curSize = s.specs[pos + 'WheelSize']; if (curSize && sizes.indexOf(curSize) < 0) sizes.push(curSize);
      var curCount = s.specs[pos + 'SpokeCount'];
      var counts = (m.counts[pos] || []).filter(function (c) { if (c === curCount) return true; var t = Object.assign({}, s.specs); t[pos + 'SpokeCount'] = c; var v = validCounts(t, cap(pos)); return v.rimCount > 0 && v.hubCount > 0; });
      var other = s.specs[otherWheel(pos) + 'WheelSize'];
      return group(pos, pos + 'WheelSize', cap(pos) + ' Wheel Size', sizes, function (sz) { return pairingActive() && !!other && !pairOk(pos, sz, other); }) +
        group(pos, pos + 'SpokeCount', cap(pos) + ' Spoke Count', counts, null);
    };
    // One row of four groups: Front Wheel Size | Front Spoke Count | Rear Wheel Size | Rear Spoke Count (each wheel's pair in its
    // classic column; i9-system.css lays the pairs out side by side). No Brake Style card: the hub sets it.
    front.innerHTML = showF ? column('front') : '';
    rear.innerHTML = showR ? column('rear') : '';
    center.innerHTML = '';
    front.style.display = showF ? 'flex' : 'none'; rear.style.display = showR ? 'flex' : 'none'; center.style.display = 'none';
    wrapper.classList.toggle('build-type-wheel-set', bT === 'Wheel Set'); wrapper.classList.toggle('build-type-single', bT !== 'Wheel Set');
    sec.classList.remove('i9-hidden'); sec.style.display = 'block';
  }

  function validCounts(testSpecs, pos) {
    var posL = pos.toLowerCase(), posPascal = cap(posL);
    var m = CFG.hubModels[testSpecs.systemHub] || null;
    var cur = bs() && bs().specs ? bs().specs.systemHub : null;
    // The caches hold only the selected model's products; a hypothetical other model cannot be counted, so treat it as available.
    if (m && cur && m.label !== cur) return { rimCount: 1, hubCount: 1 };
    var norm = fn('normalizeSpacing'), freehubOk = fn('isFreehubCompatible');
    var clean = function (val) { return val ? String(val).replace(/\D/g, '') : ''; };
    // The classic counters pass tests each Freehub button with { ...specs, freehub: value } (the row's data-spec-group is "freehub",
    // not "freehubType"). That per-button value must win over the selected freehubType; otherwise, right after a lone freehub was
    // auto-selected, every button tested as the selected one and the non-offered freehubs lost their unavailable look.
    var freehubWanted = Object.prototype.hasOwnProperty.call(testSpecs, 'freehub') ? testSpecs.freehub : testSpecs.freehubType;
    var rimIds = {}, hubIds = {};
    caches().rims.forEach(function (p) {
      p.variants.forEach(function (v) {
        if (v.spec_position !== posPascal && v.spec_position !== 'Front/Rear') return;
        if (!v.availableForSale) return;
        if (m && (m.ratio === '2:1' ? !isTwoToOne(v.hub_lacing_ratio) : rimVariantIsTwoToOne(v))) return;
        var sizeVal = v.spec_rim_size && v.spec_rim_size.parsedValue, want = testSpecs[posL + 'WheelSize'];
        if (want && sizeVal !== want && !(Array.isArray(sizeVal) && sizeVal.indexOf(want) >= 0)) return;
        if (testSpecs[posL + 'SpokeCount'] && clean(rimHoleCount(v.spec_spoke_count) || v.spec_spoke_count) !== clean(testSpecs[posL + 'SpokeCount'])) return;
        rimIds[p.id] = true;
      });
    });
    caches().hubs.forEach(function (p) {
      if (!isSystemHubProduct(p)) return;
      if (m && modelFor(p) !== m.label) return;
      p.variants.forEach(function (v) {
        if (v.spec_position !== posPascal && v.spec_position !== 'Front/Rear') return;
        if (!v.availableForSale) return;
        if (testSpecs[posL + 'HubSpacing'] && norm(v.spec_hub_spacing) !== norm(testSpecs[posL + 'HubSpacing'])) return;
        if (testSpecs[posL + 'SpokeCount'] && clean(v.spec_spoke_count) !== clean(testSpecs[posL + 'SpokeCount'])) return;
        if (testSpecs.brakeStyle && !((v.spec_brake_interface || []).indexOf(testSpecs.brakeStyle) >= 0)) return;
        if (pos === 'Rear' && freehubWanted && !freehubOk(freehubWanted, freehubListOf(v, p))) return; // per-variant freehub list
        hubIds[p.id] = true;
      });
    });
    return { rimCount: Object.keys(rimIds).length, hubCount: Object.keys(hubIds).length };
  }

  /* ------------------------------------------------------------------ Step 3 chrome */
  function syncStep3Chrome() {
    var system = isSystem();
    var nipples = document.getElementById('component-section-nipples');
    if (nipples) nipples.style.display = system ? 'none' : '';
    var qb = document.getElementById('qb-dropdown-wrapper');
    if (qb) qb.style.display = system ? 'none' : '';
    var adj = document.getElementById('spec-adjuster-section');
    if (adj) adj.classList.remove('i9-hidden'); // the quick row is rendered for both builders (renderQuickSpecAdjusters -> renderQuickAdjusters for System)
    var block = document.getElementById('i9-studio-block'), notice = document.getElementById('i9-studio-notice');
    if (block) block.style.display = system && notice && notice.style.display !== 'none' && notice.textContent ? '' : 'none'; // only carries the notice line
    if (!system) destroyPreviews();
  }
  function clearNipples() {
    var s = bs(); if (!s) return;
    ['front', 'rear'].forEach(function (pos) {
      if (s.components[pos + 'Nipples']) { s.components[pos + 'Nipples'] = null; }
      var l = document.getElementById(pos + '-nipples-loading'); if (l) l.style.display = 'none';
    });
    var sec = document.getElementById('component-section-nipples'); if (sec) sec.style.display = 'none';
  }
  // The vendor preselected in a Step 3 list. Hubs and spokes: Industry Nine. Rims: like the Standard builder, nothing is preselected ("Click to select a vendor", so the customer
  // sees there are several brands) when more than one vendor has a rim that fits the chosen size and hole count for this wheel; with a single brand it is preselected.
  function defaultVendor(componentKey) {
    if (!isSystem()) return null;
    if (!componentKey || !/Rim$/.test(componentKey)) return CFG.vendorName;
    if (bs() && bs().preset) return CFG.vendorName; // a product-page preset (Industry Nine rims) selects its rim by clicking inside this vendor
    var s = bs(), pos = /^rear/i.test(componentKey) ? 'Rear' : 'Front', size = s && s.specs[pos.toLowerCase() + 'WheelSize'], holes = s && s.specs[pos.toLowerCase() + 'SpokeCount'];
    var vendors = {};
    (caches().rims || []).forEach(function (p) {
      var ok = p.variants.some(function (v) {
        if (!v.availableForSale) return false;
        if (v.spec_position !== pos && v.spec_position !== 'Front/Rear') return false;
        var sv = v.spec_rim_size && v.spec_rim_size.parsedValue;
        if (size && !(Array.isArray(sv) ? sv.indexOf(size) >= 0 : sv === size)) return false;
        return !holes || String(v.spec_spoke_count) === String(holes);
      });
      if (ok) vendors[p.vendor] = true;
    });
    var names = Object.keys(vendors);
    return names.length > 1 ? null : (names[0] || CFG.vendorName);
  }

  /* ------------------------------------------------------------------ System spoke product and colors */
  function spokeProduct() { return caches().spokes.find(isSystemSpokeProduct) || null; }
  function spokeVariants() {
    var p = spokeProduct(); if (!p) return [];
    return p.variants.filter(function (v) { return v.availableForSale && colorOf(v) && (lengthOf(v) === '-' || lengthOf(v) === null); });
  }
  function spokeVariantFor(color) { return spokeVariants().find(function (v) { return colorOf(v) === color; }) || null; }
  function spokeColorNames() { var seen = {}, out = []; spokeVariants().forEach(function (v) { var c = colorOf(v); if (!seen[c]) { seen[c] = 1; out.push(c); } }); return out; }
  function spokeColors() { return spokeColorNames().map(colorEntry); }
  function hubProductFor(pos) {
    var s = bs(); var comp = s && s.components[pos + 'Hub'];
    if (comp && comp.productId) { var p = caches().hubs.find(function (x) { return x.id === comp.productId; }); if (p) return p; }
    var list = systemHubProducts(pos); return list[0] || null;
  }
  function hubVariantsFor(pos) {
    var s = bs(); var p = hubProductFor(pos); if (!s || !p) return [];
    var posPascal = cap(pos), norm = fn('normalizeSpacing');
    var count = s.specs[pos + 'SpokeCount'], spacing = s.specs[pos + 'HubSpacing'];
    return p.variants.filter(function (v) {
      if (!v.availableForSale) return false;
      if (v.spec_position !== posPascal && v.spec_position !== 'Front/Rear') return false;
      if (count && String(v.spec_spoke_count) !== String(count)) return false;
      if (spacing && norm(v.spec_hub_spacing) !== norm(spacing)) return false;
      if (!hubVariantAllowed(v, pos, p)) return false; // rear: only colors whose variant lists the chosen freehub
      return !!colorOf(v);
    });
  }
  function hubColors(pos) {
    var seen = {}, out = [];
    hubVariantsFor(pos).forEach(function (v) { var c = colorOf(v); if (c && !seen[c]) { seen[c] = 1; out.push(colorEntry(c)); } });
    var cur = currentHubColor(pos);
    if (cur && !seen[cur]) out.unshift(colorEntry(cur));
    return out;
  }
  function currentHubColor(pos) { var s = bs(); var comp = s && s.components[pos + 'Hub']; if (!comp) return null; var o = comp.selectedOptions && comp.selectedOptions.find(function (x) { return x.name && x.name.toLowerCase() === 'color'; }); return o ? o.value : null; }

  /* ------------------------------------------------------------------ Studio config and value */
  function sideCounts(pos, total) {
    var m = model(); var ratio = m ? m.ratio : '1:1';
    if (!total) total = m ? parseInt(m.counts[pos][0], 10) : 32;
    if (ratio === '2:1') {
      var high = Math.round(total * 2 / 3), low = total - high;
      return pos === 'rear' ? { drive: high, rotor: low } : { drive: low, rotor: high }; // rear high side = drive, front high side = rotor
    }
    return { drive: Math.ceil(total / 2), rotor: Math.floor(total / 2) };
  }
  // The rim's brand for the Studio's photo ring (config.rimImage): Industry Nine rims get the I9 ring, every other brand (We Are One and the rest) the We Are One
    // ring; no rim chosen yet = the We Are One ring. System rims are displayed under Industry Nine, so the product's original vendor counts (the cached rim list
    // keeps it); a rim whose title starts with "We Are One" is a We Are One rim whatever the vendor field says.
  function rimBrand(pos) {
    var s = bs(), comp = s && s.components[pos + 'Rim']; if (!comp) return null;
    var p = comp.productId ? (caches().rims || []).find(function (x) { return x.id === comp.productId; }) : null;
    if (/^\s*we are one\b/i.test(comp.title || '')) return 'We Are One';
    return (p && (p.originalVendor || p.vendor)) || comp.originalVendor || comp.vendor || null;
  }
  // Valve stem color per wheel (Studio config.valveColor): set by setValveColor, nothing sets it yet (the Studio draws its dark default).
  var valveColors = {};
  function setValveColor(a, b) {
    var m = {}; if (a && typeof a === 'object') m = a; else if (a === 'front' || a === 'rear') m[a] = b; else if (typeof a === 'string') m = { front: a, rear: a };
    ['front', 'rear'].forEach(function (k) { if (k in m) { if (m[k]) valveColors[k] = String(m[k]); else delete valveColors[k]; } });
    refreshPreviews();
  }
  function rimImageFor(pos) { try { return global.LoamStudio && typeof global.LoamStudio.rimImage === 'function' ? global.LoamStudio.rimImage(rimBrand(pos), { position: pos }) : null; } catch (e) { return null; } }
  function wheelConfig(pos) {
    var s = bs(); var m = model() || CFG.hubModels.Hydra2; var sample = CFG.sample[m.label] || CFG.sample.Hydra2;
    var hubProd = hubProductFor(pos), rim = s.components[pos + 'Rim'];
    var total = spokeCount(pos) || parseInt(m.counts[pos][0], 10);
    var n = sideCounts(pos, total);
    var pcdDrive = hubProd && parseFloat(hubProd.hub_flange_diameter_right), pcdRotor = hubProd && parseFloat(hubProd.hub_flange_diameter_left); // right = drive side
    var erd = rim && parseFloat(rim.rim_erd);
    // I9-SYSTEM: TODO real data. Crossing per side is not in Shopify yet; flange diameters fall back to the sample when the metafields are blank.
    var wc = {
      hubName: m.label,
      hubColor: currentHubColor(pos) || (hubColors(pos)[0] || {}).name || CFG.defaultBase,
      erd: isFinite(erd) && erd > 0 ? erd : sample.erd,
      rimHoles: total,
      drive: { n: n.drive, pcd: isFinite(pcdDrive) && pcdDrive > 0 ? pcdDrive : sample[pos].drive.pcd, cross: sample[pos].drive.cross },
      rotor: { n: n.rotor, pcd: isFinite(pcdRotor) && pcdRotor > 0 ? pcdRotor : sample[pos].rotor.pcd, cross: sample[pos].rotor.cross }
    };
    var ri = rimImageFor(pos); if (ri) wc.rimImage = ri; // photo rim (a Studio without it ignores the key)
    if (valveColors[pos]) wc.valveColor = valveColors[pos];
    return wc;
  }
  var feeCents = null, studioFrom = null, reopening = false;
  function feeDollars() { return (feeCents || CFG.fee.fallbackCents) / 100; }
  // "vendor + model" of a rim from its product title, for the Studio heading: everything before the material word (Carbon / Alloy / Aluminum), without a trailing
  // "(Enduro)" style tag: "DT Swiss EX 471 Alloy Rim (Enduro)" -> "DT Swiss EX 471", "We Are One TRIAD Carbon Rim (Enduro)" -> "We Are One TRIAD". No material word: the title
  // minus a trailing " Rim" and any brackets.
  function rimModelName(title) {
    var t = String(title == null ? '' : title).replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
    var m = /^(.*?)\s+(carbon|alloy|aluminum|aluminium)\b/i.exec(t);
    var base = (m ? m[1] : t.replace(/\s+rim\b.*$/i, '')).trim();
    return base || t;
  }
  // The Studio heading's rim part: one name when both wheels have the same rim, "front X, rear Y" when they differ; '' while no rim is chosen.
  function studioRimLabel() {
    var s = bs(), c = (s && s.components) || {}, names = {};
    positions().forEach(function (pos) { var r = c[pos + 'Rim']; if (r && r.title) names[pos] = rimModelName(r.title); });
    var f = names.front, r = names.rear;
    if (f && r) return f === r ? f : 'front ' + f + ', rear ' + r;
    return f || r || '';
  }
  function studioConfig(opts) {
    var s = bs(); var wheels = {}, hubs = {};
    positions().forEach(function (pos) { wheels[pos] = wheelConfig(pos); hubs[pos] = hubColors(pos); });
    var m = model();
    var cfg = {
      title: (studioRimLabel() ? studioRimLabel() + ' / ' : '') + 'I9 ' + (m ? m.label : 'System') + ' ' + DOT + ' ' + (positions().length === 2 ? 'front and rear wheels' : positions()[0] + ' wheel'),
      fee: { amount: feeDollars(), label: 'custom spoke color configuration' },
      // The fee explanation popup (config.feeInfo; a Studio without it ignores the key). The amount is the System fee variant's price.
      feeInfo: { title: CFG.feeInfo.title, rows: CFG.feeInfo.rows.map(function (r) { return { label: r.label, amount: r.fee ? feeDollars() : 0, when: r.when }; }), note: CFG.feeInfo.note, idle: CFG.feeInfo.idle },
      spokeColors: spokeColors(),
      hubColors: hubs,
      wheels: wheels,
      defaultBase: spokeColorNames().indexOf(CFG.defaultBase) >= 0 ? CFG.defaultBase : spokeColorNames()[0],
      defaultAccent: spokeColorNames().indexOf(CFG.defaultAccent) >= 0 ? CFG.defaultAccent : spokeColorNames()[1] || spokeColorNames()[0],
      startWheel: positions().length === 2 ? 'both' : positions()[0],
      // "Send feedback or report a problem" opens Jerry's Pify contact form above the Studio (Agent A's optional hook; the capture-phase
      // fallback below covers a Studio build that does not call it yet).
      onFeedbackRequest: requestFeedback,
      arrowImages: CFG.arrowImages // rotate-button images for the pattern tiles (Studio support by Agent A; ignored until it lands)
    };
    if (opts && opts.onFeedback) cfg.onFeedback = opts.onFeedback;
    return cfg;
  }

  // The feedback popup (Pify form 8883) lives in assets/feedback-popup.js, loaded lazily by assets/feedback-link.js, shared with the
  // builder's bottom link. The Studio's link calls it through config.onFeedbackRequest and gets focus back when it closes.
  function requestFeedback() { var link = document.querySelector('.lls-overlay [data-act="fbopen"]'); if (global.LoamFeedback) global.LoamFeedback.open(link); else warn('feedback link module not loaded'); }
  function baseColorOf(pos) { var s = bs(); var c = s && s.components[pos + 'Spokes']; return c && c.selectedColor ? c.selectedColor : null; }
  function filled(n, v) { var a = []; for (var i = 0; i < n; i++) a.push(v); return a; }
  // Builds or repairs system.studio so each active wheel has a base and spoke arrays of the current length. Returns the wheels that were reset.
  function ensureValue(cfg) {
    var s = bs(); var sys = ensureSystemState(); if (!s || !sys) return [];
    if (!global.LoamStudio || !spokeColorNames().length) return [];
    if (!positions().length) return [];
    cfg = cfg || studioConfig();
    var fresh;
    try { fresh = global.LoamStudio.defaults(cfg); } catch (e) { warn('Studio defaults failed: ' + e.message); return []; }
    var v = sys.studio; var reset = [], replaced = 0, names = spokeColorNames();
    if (!v || v.v !== 1 || !v.spokes) { v = fresh; reset = []; }
    v.hubColor = v.hubColor || {}; v.base = v.base || {}; v.spokes = v.spokes || {};
    positions().forEach(function (pos) {
      var want = cfg.wheels[pos], base = baseColorOf(pos) || (v.base[pos] && v.base[pos].drive) || cfg.defaultBase;
      var cur = v.spokes[pos];
      var ok = cur && Array.isArray(cur.drive) && Array.isArray(cur.rotor) && cur.drive.length === want.drive.n && cur.rotor.length === want.rotor.n;
      if (!ok) {
        if (cur) reset.push(pos);
        v.spokes[pos] = { drive: filled(want.drive.n, base), rotor: filled(want.rotor.n, base) };
        v.base[pos] = { drive: base, rotor: base };
      }
      if (!v.base[pos]) v.base[pos] = { drive: base, rotor: base };
      // A saved color that is no longer sold (variant gone or not available for sale) has no cart variant, and spokeCartLines would
      // silently skip those spokes: replace it by that side's base color (or the default base) so the order stays complete.
      SIDES.forEach(function (side) {
        if (names.indexOf(v.base[pos][side]) < 0) { v.base[pos][side] = names.indexOf(base) >= 0 ? base : cfg.defaultBase; replaced++; }
        var sideBase = v.base[pos][side];
        v.spokes[pos][side] = v.spokes[pos][side].map(function (c) { if (names.indexOf(c) >= 0) return c; replaced++; return sideBase; });
      });
      var hc = currentHubColor(pos); if (hc) v.hubColor[pos] = hc; else if (!v.hubColor[pos]) v.hubColor[pos] = want.hubColor;
    });
    Object.keys(v.spokes).forEach(function (pos) { if (positions().indexOf(pos) < 0) { delete v.spokes[pos]; delete v.base[pos]; delete v.hubColor[pos]; } });
    if (!v.accent || names.indexOf(v.accent) < 0) v.accent = cfg.defaultAccent;
    sys.studio = v;
    // The reset can happen inside a revalidation pass (hub/rim swapped to the new count), so the notice is raised here, once, and stays until the Studio opens.
    var msgs = reset.map(function (w) { return CFG.text.resetNotice.replace('{wheel}', w); });
    if (replaced) msgs.push(CFG.text.replacedNotice);
    if (msgs.length) showNotice(msgs.join(' '), true);
    return reset;
  }
  function derive(value) {
    if (global.LoamStudio && value) return global.LoamStudio.derive(value, { fee: { amount: feeDollars() } });
    return { counts: {}, custom: {}, customCount: 0, fee: 0 };
  }
  function countsFor(pos) {
    var s = bs(); var sys = s && s.system; var value = sys && sys.studio;
    if (value && value.spokes && value.spokes[pos]) return derive(value).counts[pos] || {};
    var base = baseColorOf(pos); var out = {}; if (base) out[base] = spokeCount(pos) || 0; return out;
  }
  function isCustom(pos) { var c = countsFor(pos); return Object.keys(c).filter(function (k) { return c[k] > 0; }).length > 1; }
  function colorText(pos) {
    var c = countsFor(pos), base = baseColorOf(pos);
    var names = Object.keys(c).filter(function (k) { return c[k] > 0; }).sort(function (a, b) { if (a === base) return -1; if (b === base) return 1; return c[b] - c[a]; });
    if (!names.length) return base || '';
    if (names.length === 1) return names[0];
    return names.map(function (n) { return n + ' ' + TIMES + c[n]; }).join(', ');
  }

  /* ------------------------------------------------------------------ Step 3: spokes UI */
  var previews = {};
  function destroyPreviews() { Object.keys(previews).forEach(function (k) { try { previews[k].handle.destroy(); } catch (e) { /* ignore */ } }); previews = {}; }
  function refreshPreviews() {
    var s = bs(); if (!s || !isSystem() || !global.LoamStudio || !spokeColorNames().length) return;
    var cfg = studioConfig(); ensureValue(cfg);
    positions().forEach(function (pos) {
      var host = document.getElementById(pos + '-spokes-image-container'); if (!host) return;
      var slot = host.querySelector('.i9-preview-wheel'); if (!slot) return;
      var key = pos, sig = JSON.stringify(cfg.wheels[pos]) + '|' + JSON.stringify(cfg.spokeColors) + '|' + JSON.stringify(cfg.hubColors[pos]) + '|' + previewSize();
      if (previews[key] && previews[key].sig === sig && previews[key].slot === slot) { previews[key].handle.update(s.system.studio); return; }
      if (previews[key]) { try { previews[key].handle.destroy(); } catch (e) { /* ignore */ } }
      try {
        previews[key] = { sig: sig, slot: slot, handle: global.LoamStudio.preview(slot, { config: cfg, value: s.system.studio, wheel: pos, size: previewSize(), pov: 'rotor', dark: true }) };
      } catch (e) { warn('preview failed for ' + pos + ': ' + e.message); slot.innerHTML = ''; }
    });
  }
  // per-spoke weight from the variant (product-level fallback); the stage shows weight x spoke count
  function weightOf(variant) { var p = spokeProduct(); var w = variant && variant.weight_g != null ? variant.weight_g : (p && p.weight_g != null ? p.weight_g : null); return (w === null || w === undefined || isNaN(w)) ? null : Number(w); }
  function otherPos(pos) { return pos === 'front' ? 'rear' : 'front'; }
  function previewSize() { return (global.innerWidth && global.innerWidth < 768) ? 240 : CFG.previewSize; }
  // The Step 3 spokes card (design-reference/SpokesAlt.dc.html, variant A): one dark stage with "Left side view" + weight on top,
  // the live wheel in dark mode, a row of color chips built from the live counts, and the orange Studio button. The base color is
  // changed only inside the Studio; the only other control is the small "Click to match" pill when the two wheels differ.
  var STUDIO_ICON = '<svg class="i9-stage-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#1d1f23" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="10" cy="10" r="8"></circle><circle cx="10" cy="10" r="1.6"></circle><path d="M10 2v6.4M10 11.6V18M2 10h6.4M11.6 10H18M4.3 4.3l4.5 4.5M11.2 11.2l4.5 4.5M15.7 4.3l-4.5 4.5M8.8 11.2l-4.5 4.5"></path></svg>';
  function chipsHtml(pos) {
    var c = countsFor(pos), base = baseColorOf(pos);
    var names = Object.keys(c).filter(function (k) { return c[k] > 0; }).sort(function (a, b) { if (a === base) return -1; if (b === base) return 1; return c[b] - c[a]; });
    if (!names.length && base) { names = [base]; c[base] = spokeCount(pos) || 0; }
    return '<div class="i9-chips">' + names.map(function (n) { return '<span class="i9-chip"><span class="i9-chip-dot" style="background:' + (hexFor(n) || '#9aa0a6') + '"></span>' + esc(n) + ' ' + TIMES + c[n] + '</span>'; }).join('') + '</div>';
  }
  function stageHtml(pos) {
    var s = bs(); var comp = s.components[pos + 'Spokes']; var w = comp ? comp.weight_g : null; var count = spokeCount(pos) || 32;
    var wn = (w === null || w === undefined || w === '') ? NaN : Number(w), wtTotal = Math.round(wn * count);
    var wt = (isFinite(wn) && wn > 0 && isFinite(wtTotal)) ? 'Wt: ' + wtTotal + 'g' : ''; // no weight shown when it is missing or not a number
    var other = otherPos(pos), pill = '';
    if (s.buildType === 'Wheel Set' && s.components[other + 'Spokes'] && baseColorOf(other) && baseColorOf(other) !== baseColorOf(pos)) {
      pill = '<button type="button" class="i9-match-pill" data-i9-action="match" data-position="' + pos + '">Click to match to ' + cap(other) + '</button>';
    }
    return '<div class="i9-stage">' + pill +
      '<div class="i9-stage-top"><span>Left side view</span><span class="selected-component-weight-display i9-wt">' + esc(wt) + '</span></div>' +
      '<div class="i9-preview-wheel"></div>' + chipsHtml(pos) +
      '<button type="button" class="i9-stage-button" data-i9-action="studio" data-position="' + pos + '">' + STUDIO_ICON + '<span>Edit in Spoke Color Studio</span></button>' +
    '</div>';
  }
  // "Match to Rear/Front" for System spokes: copies the other wheel's BASE color (never the per-spoke map) and its hub color when this
  // hub comes in it. Called by the builder's auto-pairing and match handlers with the SOURCE position (the wheel to copy from).
  function matchSpokes(sourcePosition, explicit) {
    var s = bs(); if (!s || !isSystem()) return 'not_applicable';
    if (s.buildType !== 'Wheel Set') return 'not_applicable';
    var source = String(sourcePosition || '').toLowerCase(), target = otherPos(source);
    if (source !== 'front' && source !== 'rear') return 'not_applicable';
    if (!s.components[source + 'Spokes'] || !baseColorOf(source)) return 'no_match_found';
    if (!explicit && s.components[target + 'Spokes']) return 'opposite_filled'; // auto-pairing never overwrites a wheel the customer already set
    var notices = [], color = baseColorOf(source), changed = false;
    if (color !== baseColorOf(target)) {
      if (spokeVariantFor(color)) { applyBaseChange(target, color, isCustom(target) ? 'only' : 'all'); changed = true; }
      else notices.push(color + ' is not available for the ' + target + ' wheel, so its spoke color was kept.');
    }
    var hubColor = currentHubColor(source);
    if (hubColor && s.components[target + 'Hub'] && hubColor !== currentHubColor(target)) {
      if (trySetHubColor(target, hubColor)) changed = true;
      else notices.push(hubColor + ' is not available for the ' + target + ' hub, so the hub color was kept.');
    }
    // whatever happened, both cards are redrawn from the (still System) components
    ['front', 'rear'].forEach(function (pos) { if (s.components[pos + 'Spokes']) fn('updateComponentUI')(cap(pos), 'Spokes', s.components[pos + 'Spokes']); });
    refreshPreviews();
    fn('updateMismatchHints')();
    if (notices.length) showNotice(notices.join(' '));
    return changed ? 'single_match_selected' : 'opposite_filled';
  }
  function setSpokeBase(pos, color, silent) {
    var s = bs(); var p = spokeProduct(); var v = spokeVariantFor(color); if (!s || !p || !v) { warn('no SYSTEM spoke variant for color ' + color); return false; }
    var prev = s.components[pos + 'Spokes'];
    s.components[pos + 'Spokes'] = {
      variantId: v.id, productId: p.id, productHandle: p.handle, title: p.title, variantTitle: v.title, sku: v.sku,
      price: v.price.amount, vendor: p.vendor, image: v.image, pairingKey: p.pairingKey, selectedOptions: v.selectedOptions, // the bundle price of the wheel's spokes (not per spoke)
      optionalAddonProductGids: [], quantityAvailable: v.quantityAvailable, leadTimeDays: p.leadTimeDays || 0, wheelModel: p.wheelModel,
      selectedColor: color, weight_g: weightOf(v), i9: true
    };
    if (!silent && (!prev || prev.variantId !== v.id)) { s._ui.hasChanges = true; fn('updateStep3ButtonText')(); }
    return true;
  }
  // Keeps the spoke component on the catalog's bundle variant of its color and price. A build saved before the bundle (a per-spoke variant and a unit price) is
  // repriced here whenever it is restored (Resume, Saved Builds, share link, edit from cart). The count of spokes no longer changes the price.
  function syncSpokeComponent(pos) {
    var s = bs(); var comp = s && s.components[pos + 'Spokes']; if (!comp || !comp.i9) return;
    var v = comp.selectedColor ? spokeVariantFor(comp.selectedColor) : null; if (!v) return; // not in the catalog (yet): keep what the build has
    if (comp.variantId !== v.id || comp.price !== v.price.amount || comp.unitPrice !== undefined) setSpokeBase(pos, comp.selectedColor, true);
  }
  function afterSpokeChange(pos) {
    var s = bs(); if (!s) return;
    syncSpokeComponent(pos);
    fn('calculateWheelCosts')(cap(pos));
    fn('updateComponentUI')(cap(pos), 'Spokes', s.components[pos + 'Spokes']);
    // the other wheel's card shows or hides its "Match to …" link depending on this wheel's base
    var o = otherPos(pos);
    if (s.buildType === 'Wheel Set' && s.components[o + 'Spokes'] && s.components[o + 'Spokes'].i9) fn('updateComponentUI')(cap(o), 'Spokes', s.components[o + 'Spokes']);
    fn('updateSubtotal')();
    fn('validateStep3')();
    fn('handleComponentChangeForLeadTime')();
    fn('saveLastActiveBuild')();
  }
  // Sets a wheel's base color from outside the Studio (used by "Click to match"). mode 'only' recolors the old-base spokes and keeps
  // other colors; 'all' makes every spoke the new color. Inside the Studio the base is changed by the Studio itself.
  function applyBaseChange(pos, color, mode) {
    var s = bs(); var sys = ensureSystemState(); if (!s || !sys) return;
    ensureValue();
    var v = sys.studio, old = baseColorOf(pos);
    if (v && v.spokes[pos]) {
      SIDES.forEach(function (side) {
        if (mode === 'all') { v.base[pos][side] = color; v.spokes[pos][side] = v.spokes[pos][side].map(function () { return color; }); }
        else { if (v.base[pos][side] === old) v.base[pos][side] = color; v.spokes[pos][side] = v.spokes[pos][side].map(function (c) { return c === old ? color : c; }); }
      });
    }
    setSpokeBase(pos, color, false);
    afterSpokeChange(pos);
    refreshPreviews();
  }
  async function renderSpokes(position, isChange, refs) {
    var s = bs(); var pos = position.toLowerCase(); if (!s) return;
    if (refs && refs.loading) refs.loading.style.display = 'block';
    var cf = B() && B().componentFetchPromises;
    if (!caches().spokes.length) { try { if (cf && cf.spokes) await cf.spokes; if (!caches().spokes.length) await fn('fetchSpokeProducts')(); } catch (e) { warn('spoke fetch failed: ' + e.message); } }
    if (refs && refs.loading) refs.loading.style.display = 'none';
    s._ui.activeVendor[pos + 'Spokes'] = CFG.vendorName;
    var p = spokeProduct();
    if (refs && refs.filters) fn('displayComponentVendorFilters')(pos + 'Spokes', p ? [p] : [], refs.filters, position);
    if (refs && refs.actionButtonsContainer) { refs.actionButtonsContainer.innerHTML = ''; refs.actionButtonsContainer.style.display = 'none'; }
    if (s._ui.isChangingComponent && s._ui.isChangingComponent.position === position && s._ui.isChangingComponent.type === 'Spokes') fn('cancelComponentChange')();
    if (!p) { if (refs && refs.optionsContainer) { refs.optionsContainer.style.display = 'flex'; refs.optionsContainer.innerHTML = '<p>' + esc('Industry Nine SYSTEM Spokes product not found. Check that it is published and tagged component:spoke + ' + CFG.systemTag + '.') + '</p>'; } return; }
    // Default base color so the column, previews and cart are always consistent (spec: the base is highlighted from the start).
    if (!s.components[pos + 'Spokes'] || !s.components[pos + 'Spokes'].i9) {
      var defaultColor = spokeColorNames().indexOf(CFG.defaultBase) >= 0 ? CFG.defaultBase : spokeColorNames()[0];
      if (defaultColor) { setSpokeBase(pos, defaultColor, true); fn('calculateWheelCosts')(position); }
    } else syncSpokeComponent(pos);
    fn('updateComponentUI')(position, 'Spokes', s.components[pos + 'Spokes']); // decorateComponentUI draws the dropdown + preview
    fn('validateStep3')();
  }
  // Called from updateComponentUI (hook) for every component; only acts for System spokes and hubs.
  // The Studio card (wheel thumbnail + "Edit in Spoke Color Studio") shows for a wheel only while BOTH its rim and its hub are chosen. The spokes are
  // auto-selected as before; only the card waits. syncStage runs whenever the wheel's rim or hub is set, changed or cleared, and when its spokes are drawn.
  function stageReady(pos) { var s = bs(); return !!(s && isSystem() && s.components[pos + 'Rim'] && s.components[pos + 'Hub']); }
  function bindStage(img, pos) {
    img.style.display = 'flex'; img.dataset.i9Static = '1'; img.style.cursor = 'default'; img.title = '';
    if (img.dataset.i9Bound) return;
    img.dataset.i9Bound = '1';
    img.addEventListener('click', function (e) {
      var action = e.target.closest('[data-i9-action]');
      if (!action) {
        // The card itself (mouse / touch) opens the Studio on this wheel; any control inside the card handles its own click.
        if (e.target.closest('button, a, input, select, label') || !e.target.closest('.i9-stage')) return;
        e.preventDefault(); openStudio(pos); return;
      }
      e.preventDefault(); e.stopPropagation();
      if (action.dataset.i9Action === 'match') matchSpokes(otherPos(pos), true);
      else if (action.dataset.i9Action === 'studio') openStudio(pos);
    });
  }
  function syncStage(pos) {
    var s = bs(); if (!s || !isSystem()) return;
    var comp = s.components[pos + 'Spokes']; if (!comp || !comp.i9) return; // the spokes' own draw (decorateComponentUI) calls this once they exist
    var img = document.getElementById(pos + '-spokes-image-container'); if (!img) return;
    if (stageReady(pos)) {
      img.innerHTML = stageHtml(pos); bindStage(img, pos);
    } else {
      if (previews[pos]) { try { previews[pos].handle.destroy(); } catch (e) { /* ignore */ } delete previews[pos]; }
      img.innerHTML = ''; img.style.display = 'none'; delete img.dataset.i9Static;
    }
    refreshPreviews();
  }
  function decorateComponentUI(position, typePascal, componentData) {
    if (!isSystem()) return;
    var pos = position.toLowerCase();
    if (!componentData) { if (typePascal === 'Rim' || typePascal === 'Hub') syncStage(pos); return; } // a rim or hub was cleared: the card goes with it
    if (typePascal === 'Hub') {
      // The line reads like the hub list: full product title plus the variant title, e.g.
      // "Industry Nine Hydra2 SYSTEM Front Hub - 6-Bolt (28h / Black / 15x110mm (20mm Hub))", linked, then " - $240.00".
      // Every path (click, auto-pairing, Studio hub color, Resume / edit-from-cart / share link) comes through updateComponentUI, so this is the one place.
      var span = document.querySelector('#' + pos + '-hub-selection span');
      if (span) {
        var a = span.querySelector('a');
        var vt = componentData.variantTitle || (componentData.selectedOptions || []).map(function (o) { return o.value; }).filter(function (v) { return v && v.toLowerCase() !== 'default title'; }).join(' / ');
        var text = esc(componentData.title || modelName() || '') + (vt ? ' (' + esc(vt) + ')' : '');
        var priceD = componentData.price ? ' - ' + money(componentData.price) : '';
        span.innerHTML = 'Selected ' + esc(position) + ' Hub: ' + (a ? '<a href="' + esc(a.getAttribute('href')) + '" target="_blank" title="' + esc(a.getAttribute('title') || '') + '"><strong>' + text + '</strong></a>' : '<strong>' + text + '</strong>') + priceD;
      }
      onHubChanged(pos);
      return;
    }
    if (typePascal === 'Rim') { syncStage(pos); refreshPreviews(); return; }
    if (typePascal !== 'Spokes' || !componentData.i9) return;
    var sub = document.getElementById(pos + '-spokes-subsection'), opts = document.getElementById(pos + '-spokes-options-container'), img = document.getElementById(pos + '-spokes-image-container'), sel = document.getElementById(pos + '-spokes-selection');
    if (!sub || !opts || !img || !sel) return;
    sub.classList.add('i9-spokes');
    opts.innerHTML = ''; opts.style.display = 'none'; // no list in Step 3: colors are chosen in the Studio
    if (stageReady(pos)) { img.innerHTML = stageHtml(pos); bindStage(img, pos); }
    else { img.innerHTML = ''; img.style.display = 'none'; delete img.dataset.i9Static; } // no card until this wheel has both its rim and its hub
    var span = sel.querySelector('span');
    if (span) {
      var a = span.querySelector('a');
      var title = esc(componentData.title) + ' (' + esc(colorText(pos)) + ')';
      span.innerHTML = 'Selected ' + esc(position) + ' Spokes: ' + (a ? '<a href="' + esc(a.getAttribute('href')) + '" target="_blank" title="' + esc(a.getAttribute('title') || '') + '"><strong>' + title + '</strong></a>' : '<strong>' + title + '</strong>') + ' - ' + money(componentData.price);
    }
    var fee = sel.querySelector('.i9-fee-line');
    if (isCustom(pos)) {
      if (!fee) { fee = document.createElement('div'); fee.className = 'i9-fee-line'; sel.appendChild(fee); }
      fee.innerHTML = 'Selected ' + esc(position) + ' Spoke Color Configuration: <strong>' + esc(CFG.fee.name) + '</strong> - ' + money(Math.round(feeDollars() * 100));
    } else if (fee) fee.remove();
    refreshPreviews();
  }
  function onHubChanged(pos) {
    var s = bs(); if (!s || !isSystem()) return;
    var sys = s.system; var hc = currentHubColor(pos);
    if (sys && sys.studio && hc) { sys.studio.hubColor = sys.studio.hubColor || {}; sys.studio.hubColor[pos] = hc; }
    syncStage(pos); // the card appears when this hub completes the rim + hub pair (it redraws the previews too)
    refreshPreviews();
  }
  function onRimChanged() { refreshPreviews(); }

  /* ------------------------------------------------------------------ hub color from the Studio back into Step 3 */
  function trySetHubColor(pos, color) {
    var s = bs(); var cur = s.components[pos + 'Hub']; if (!s || !cur || currentHubColor(pos) === color) return true;
    var p = hubProductFor(pos); if (!p) return false;
    var v = hubVariantsFor(pos).find(function (x) { return colorOf(x) === color; });
    if (!v) return false;
    var posPascal = cap(pos);
    var comp = {
      variantId: v.id, productId: p.id, productHandle: p.handle, title: p.title, variantTitle: v.title, sku: v.sku, price: v.price.amount, vendor: p.vendor, image: v.image,
      pairingKey: p.pairingKey, tags: p.tags, spec_spoke_count: String(v.spec_spoke_count), spec_brake_interface: v.spec_brake_interface, spec_hub_spacing: v.spec_hub_spacing,
      spec_freehub: pos === 'rear' ? s.specs.freehubType : null, selectedOptions: v.selectedOptions, optionalAddonProductGids: p.optionalAddonProductGidsFromProduct || [],
      leadTimeDays: p.leadTimeDays || 0, wheelModel: p.wheelModel, quantityAvailable: v.quantityAvailable, weight_g: v.weight_g != null ? v.weight_g : (p.weight_g != null ? p.weight_g : null)
    };
    s.components[pos + 'Hub'] = comp;
    if (pos === 'rear') fn('setComponentFreehubForHub')(Object.assign({}, v, { freehubVariantMap: p.freehubVariantMap || {} }));
    s._ui.hasChanges = true;
    fn('updateStep3ButtonText')();
    fn('calculateWheelCosts')(posPascal);
    fn('updateComponentUI')(posPascal, 'Hub', comp);
    fn('updateSubtotal')();
    fn('validateStep3')();
    fn('handleComponentChangeForLeadTime')();
    fn('saveLastActiveBuild')();
    return true;
  }

  /* ------------------------------------------------------------------ the Studio */
  function showNotice(text, persist) {
    var el = document.getElementById('i9-studio-notice'); if (!el) return;
    var block = document.getElementById('i9-studio-block');
    var set = function (show) { el.style.display = show ? '' : 'none'; if (block) block.style.display = show && isSystem() ? '' : 'none'; };
    el.textContent = text; set(!!text);
    clearTimeout(showNotice.t);
    if (text && !persist) showNotice.t = setTimeout(function () { set(false); }, 8000);
  }
  function applyStudioValue(value, info, closing) {
    var s = bs(); var sys = ensureSystemState(); if (!s || !sys) return;
    sys.studio = value;
    var notices = [];
    // Read every requested hub color before touching the builder: updating one hub re-runs ensureValue (through updateComponentUI ->
    // refreshPreviews), which writes the builder's current hub colors back into this same value object and would hide the other
    // wheel's request ("Hub · Both wheels" then changed only the front hub).
    var wantHub = Object.assign({}, value.hubColor || {}), hubKept = false;
    positions().forEach(function (pos) {
      // base color per wheel (drive side) drives the dropdown and the cart variant
      var base = value.base && value.base[pos] ? value.base[pos].drive : null;
      if (base && base !== baseColorOf(pos)) setSpokeBase(pos, base, false);
      // hub color flows back to the Step 3 hub selection
      var hc = wantHub[pos];
      if (hc && hc !== currentHubColor(pos)) {
        if (!trySetHubColor(pos, hc)) { var keep = currentHubColor(pos); if (keep) value.hubColor[pos] = keep; hubKept = true; notices.push(CFG.text.hubColorUnavailable.replace('{color}', hc).replace('{wheel}', pos)); }
      }
      syncSpokeComponent(pos);
      fn('calculateWheelCosts')(cap(pos));
      fn('updateComponentUI')(cap(pos), 'Spokes', s.components[pos + 'Spokes']);
    });
    fn('updateSubtotal')();
    fn('validateStep3')();
    fn('saveLastActiveBuild')();
    refreshPreviews();
    if (closing && !reopening) {
      sys.studioOpened = true; if (info && info.replaced) notices.unshift(CFG.text.replacedNotice);
      // The Studio restores focus to the button that opened it before calling onClose, but the redraw above replaced that button
      // (stageHtml rebuilds the card), so a keyboard user lost focus to the page. Focus the new button of the same column.
      var fromPos = studioFrom || positions()[0];
      setTimeout(function () { var b = document.querySelector('#' + fromPos + '-spokes-image-container [data-i9-action="studio"]'); if (b) b.focus({ preventScroll: true }); }, 0);
    }
    if (reopening) return; // the internal close before a reopen: the kept hub color was already reported
    // A hub color the builder could not set (its variant is no longer available for sale) would stay selected in the open Studio:
    // there is no API to push a value into it, so the Studio is reopened on the same tab with the kept color. The notice stays
    // on the page until the Studio is opened again, since it sits behind the modal while the Studio is up.
    if (hubKept && !closing && global.LoamStudio && global.LoamStudio.isOpen()) {
      var tabBtn = document.querySelector('.lls-overlay .lls-tab.is-on'), tab = tabBtn ? tabBtn.getAttribute('data-v') : null;
      reopening = true;
      setTimeout(function () { try { openStudio(tab); } finally { reopening = false; } }, 0);
    }
    if (notices.length) showNotice(notices.join(' '), hubKept);
  }
  // startWheel: 'front' | 'rear' opens the Studio on that wheel's tab (each column's button); otherwise both wheels / the only wheel.
  function openStudio(startWheel) {
    var s = bs(); if (!s || !isSystem() || !global.LoamStudio) { warn('Studio unavailable'); return; }
    if (!spokeColorNames().length) { showNotice('The Industry Nine SYSTEM Spokes product has no available colors.'); return; }
    var cfg = studioConfig();
    if (typeof startWheel === 'string' && positions().indexOf(startWheel) >= 0) cfg.startWheel = startWheel;
    studioFrom = cfg.startWheel === 'both' ? positions()[0] : cfg.startWheel; // the column whose button gets focus back on close
    ensureValue(cfg);
    if (!reopening) showNotice(''); // a reopen after a kept hub color leaves that notice on the page
    ensureSystemState().studioOpened = true;
    global.LoamStudio.open({
      config: cfg,
      value: s.system.studio,
      onChange: function (value, info) { applyStudioValue(value, info, false); },
      onClose: function (value, info) { applyStudioValue(value, info, true); }
    });
  }
  function addStudioReminder(warnings) {
    var s = bs(); if (!s || !isSystem()) return;
    if (s.system && s.system.studioOpened) return;
    var shown = positions().filter(stageReady)[0]; // the first wheel whose Studio card is on the page
    if (!shown) return; // no card is showing yet (no wheel has both its rim and hub), so there is nothing to point the customer to
    warnings.push({ type: 'Studio', message: CFG.text.studioReminder, targetId: shown + '-spokes-subsection' });
  }

  /* ------------------------------------------------------------------ fee, summary, cart */
  function applyColorFee(position) {
    var s = bs(); if (!s || !isSystem()) return;
    var pos = position.toLowerCase(); if (positions().indexOf(pos) < 0) return;
    if (!isCustom(pos)) return;
    var cents = feeCents || CFG.fee.fallbackCents;
    var arr = s.components[pos + 'RequiredAddons'] || (s.components[pos + 'RequiredAddons'] = []);
    if (arr.some(function (a) { return a.variantGid === CFG.fee.variantGid; })) return;
    arr.push({ name: CFG.fee.name, price: cents / 100, variantGid: CFG.fee.variantGid, costInCents: cents, i9Fee: true });
  }
  // The store's 10% code does not apply to the fee product, so the builder's estimate leaves it out too. Classic add-ons are never exempt.
  function isDiscountExempt(addon) { return !!(addon && addon.i9Fee === true && addon.variantGid === CFG.fee.variantGid); }
  function summaryDescription(type, component, pos) {
    if (!isSystem() || type !== 'Spokes' || !component || !component.i9) return null;
    return component.title + ' (' + colorText(pos) + ')' + (spokeCount(pos) ? ' ' + DOT + ' ' + spokeCount(pos) + ' spokes' : '');
  }
  function summaryFeeRows() {
    var s = bs(); if (!s || !isSystem()) return '';
    var html = '';
    positions().forEach(function (pos) {
      var fee = (s.components[pos + 'RequiredAddons'] || []).find(function (a) { return a.i9Fee; });
      if (fee) html += '<li><span class="summary-item-label-column">' + cap(pos) + ':</span><span class="summary-item-description-column"><strong>(1) ' + esc(fee.name) + '</strong></span> <span class="summary-value">(' + money(fee.costInCents) + ')</span></li>';
    });
    return html;
  }
  // Most-used color first; equal counts go to the wheel's base color, then by name (so the answer never depends on spoke order). Same rule as the Standard builder.
  function sortedColors(pos) {
    var c = countsFor(pos), base = baseColorOf(pos);
    return Object.keys(c).filter(function (k) { return c[k] > 0; }).sort(function (a, b) { if (c[b] !== c[a]) return c[b] - c[a]; if (a === base) return -1; if (b === base) return 1; return a < b ? -1 : 1; });
  }
  // The variant of the wheel's cart line: its most-used color. Prices never differ between colors; if one ever did, the line keeps the list color
  // (the builder's total comes from the list variant, so the line must cost the same).
  function lineVariantId(pos, comp) {
    var color = sortedColors(pos)[0], v = color ? spokeVariantFor(color) : null;
    if (!v || (comp.price != null && v.price && v.price.amount !== comp.price)) { if (v) warn('cart line keeps the list color: ' + color + ' is priced differently'); return comp.variantId; }
    return v.id;
  }
  // One spoke line per wheel, quantity 1, at the bundle price (copy of the Standard builder's model). The per-color counts and the per-spoke map travel in
  // the placeholder line's properties (readableProperties) and in _build; the wheel's own line also names its colors, and says it is a bundle (_spoke_bundle)
  // so the order's reader can tell it from the per-spoke lines older orders have.
  function spokeCartLines(position, baseProps) {
    var s = bs(); var pos = position.toLowerCase(); var comp = s.components[pos + 'Spokes']; if (!comp) return [];
    var toId = fn('getNumericIdFromShopifyGid'), id = lineVariantId(pos, comp);
    if (!id) { warn('no cart variant for the ' + pos + ' spokes'); return []; }
    return [{ id: toId(id), quantity: 1, properties: Object.assign({}, baseProps, { Wheel: position, 'Spoke Colors': colorText(pos), _spoke_bundle: '1' }) }];
  }
  // The System part of _build and of a saved build: the Studio value (what the customer designed) plus what the cart page needs to draw each wheel
  // without the builder: the geometry the Studio uses (wheelConfig) and the hex of every color in play. A Standard build has no System data (null).
  function snapshot() {
    var s = bs(); if (!s || !s.system) return null;
    var out = JSON.parse(JSON.stringify(s.system));
    if (!isSystem()) return out;
    out.wheels = {}; out.colors = {};
    var add = function (name) { if (name) out.colors[name] = hexFor(name); };
    positions().forEach(function (pos) {
      var w = wheelConfig(pos), comp = s.components[pos + 'Spokes'];
      out.wheels[pos] = { hubName: w.hubName, hubColor: w.hubColor, erd: w.erd, rimHoles: w.rimHoles, drive: w.drive, rotor: w.rotor };
      add(w.hubColor); add(comp && comp.selectedColor);
      var sp = out.studio && out.studio.spokes && out.studio.spokes[pos];
      if (sp) SIDES.forEach(function (side) { (sp[side] || []).forEach(add); });
    });
    return out;
  }
  function readableProperties() {
    var s = bs(); var props = {}; if (!s || !isSystem()) return props;
    var value = s.system && s.system.studio;
    positions().forEach(function (pos) {
      var P = cap(pos), counts = countsFor(pos);
      var names = Object.keys(counts).filter(function (k) { return counts[k] > 0; }).sort(function (a, b) { return counts[b] - counts[a]; });
      if (names.length) props[P + ' Spoke Colors'] = names.map(function (n) { return n + ' ' + TIMES + counts[n]; }).join(', ');
      if (isCustom(pos) && value && value.spokes && value.spokes[pos]) {
        SIDES.forEach(function (side) { props[P + ' ' + cap(side) + ' Side'] = (value.spokes[pos][side] || []).map(function (c, i) { return (i + 1) + ' ' + c; }).join(', '); });
      }
    });
    if (modelName()) props['System Hub'] = modelName();
    if (positions().indexOf('rear') >= 0 && s.specs.freehubType) props['Freehub'] = s.specs.freehubType;
    return props;
  }
  function reportBuildSize(placeholderProps) {
    try {
      var buildLen = (placeholderProps._build || '').length, total = 0;
      Object.keys(placeholderProps).forEach(function (k) { total += k.length + String(placeholderProps[k]).length; });
      return { build: buildLen, total: total }; // measured during integration (about 5 KB, far below Shopify's limits); no longer logged
    } catch (e) { return null; }
  }

  /* ------------------------------------------------------------------ Step 3 render completion (fresh, edit-from-cart, Resume, share) */
  async function afterStep3Render() {
    var s = bs(); if (!s || !isSystem()) return;
    syncStep3Chrome();
    renderQuickAdjusters(); // the quick row was drawn before the rim data was in (edit-from-cart, share link); redraw with every size
    clearNipples();
    var cf = B() && B().componentFetchPromises;
    try { if (cf && cf.spokes) await cf.spokes; } catch (e) { /* ignore */ }
    positions().forEach(function (pos) {
      if (!s.components[pos + 'Spokes'] || !s.components[pos + 'Spokes'].i9) {
        var c = spokeColorNames().indexOf(CFG.defaultBase) >= 0 ? CFG.defaultBase : spokeColorNames()[0];
        if (c) setSpokeBase(pos, c, true);
      } else syncSpokeComponent(pos);
    });
    ensureValue();
    // ensureValue may have replaced a base color that is no longer sold; the Step 3 dropdown and the cart variant follow it
    positions().forEach(function (pos) { var b = s.system && s.system.studio && s.system.studio.base[pos] ? s.system.studio.base[pos].drive : null; if (b && b !== baseColorOf(pos)) setSpokeBase(pos, b, true); });
    positions().forEach(function (pos) { fn('updateComponentUI')(cap(pos), 'Spokes', s.components[pos + 'Spokes']); if (s.components[pos + 'Hub']) fn('updateComponentUI')(cap(pos), 'Hub', s.components[pos + 'Hub']); });
    refreshPreviews();
    fn('validateStep3')();
  }

  /* ------------------------------------------------------------------ fee price from the store */
  async function loadFeePrice() {
    try {
      var q = 'query($id: ID!) { node(id: $id) { ... on ProductVariant { id title availableForSale price { amount } product { title } } } }';
      var data = await fn('storefrontApiQuery')(q, { id: CFG.fee.variantGid });
      var node = data && data.node;
      if (node && node.price && isFinite(parseFloat(node.price.amount))) { feeCents = Math.round(parseFloat(node.price.amount) * 100); if (!node.availableForSale) warn('fee variant "' + node.title + '" is not available for sale; the add to cart will fail for custom colors'); }
      else warn('fee variant not readable through the Storefront API (is it published?); using the $25.00 fallback');
    } catch (e) { warn('fee price lookup failed: ' + e.message + '; using the $25.00 fallback'); }
  }

  /* ------------------------------------------------------------------ wiring */
  document.addEventListener('DOMContentLoaded', function () {
    if (B()) loadFeePrice();
    syncStep1UI();
  });
  // The stage wheel is 250 px on desktop and 240 px on phones; redraw when a resize crosses that line (the preview keeps its build size otherwise).
  var lastPreviewSize = null;
  if (typeof global.addEventListener === 'function') global.addEventListener('resize', function () {
    clearTimeout(global.__i9ResizeT);
    global.__i9ResizeT = setTimeout(function () { var n = previewSize(); if (lastPreviewSize !== null && n !== lastPreviewSize && isSystem()) refreshPreviews(); lastPreviewSize = n; }, 200);
  });

  // a builder can also fire window.dispatchEvent(new CustomEvent('loam:valve-color', { detail: { front: '#c0392b', rear: '#2b2b2b' } }))
  if (typeof global.addEventListener === 'function') global.addEventListener('loam:valve-color', function (e) { setValveColor(e && e.detail); });

  global.I9System = {
    CFG: CFG,
    isSystem: isSystem,
    setValveColor: setValveColor,
    isAlloyRim: isAlloyRim, systemDiscountApplies: systemDiscountApplies, rimInDiscountCollections: rimInDiscountCollections, rimModelName: rimModelName, studioRimLabel: studioRimLabel, rimPairNotice: rimPairNotice, syncRimPairNotice: syncRimPairNotice,
    filterEligible: filterEligible, _rimGateBroken: function () { return rimGateBroken; },
    cachesStale: cachesStale,
    ensureFreshCaches: ensureFreshCaches,
    onSpokeInterfaceClick: onSpokeInterfaceClick,
    syncStep1UI: syncStep1UI,
    onHubModelChange: onHubModelChange,
    protectSpecs: protectSpecs,
    syncStep2UI: syncStep2UI,
    generateStep2Buttons: generateStep2Buttons,
    validCounts: validCounts,
    syncStep3Chrome: syncStep3Chrome,
    clearNipples: clearNipples,
    defaultVendor: defaultVendor,
    renderSpokes: renderSpokes,
    decorateComponentUI: decorateComponentUI,
    afterStep3Render: afterStep3Render,
    applyColorFee: applyColorFee,
    isDiscountExempt: isDiscountExempt,
    summaryDescription: summaryDescription,
    prefetch: prefetch, applyPrefetched: applyPrefetched, refillCaches: refillCaches, _prefetchState: PF, _prefetchQueries: function () { return PREFETCH_QUERIES; }, _rimErdPlausible: rimErdPlausible,
    isPairAllowed: isPairAllowed, allowedRearsFor: allowedRearsFor, allowedFrontsFor: allowedFrontsFor, onSizeCompat: onSizeCompat, onSizeClick: onSizeClick, onAdjusterClick: onAdjusterClick, renderQuickAdjusters: renderQuickAdjusters, markSizePairing: markSizePairing, _planOtherSize: planOtherSize, _sizesFor: sizesFor,
    summaryFeeRows: summaryFeeRows,
    spokeCartLines: spokeCartLines,
    readableProperties: readableProperties,
    snapshot: snapshot,
    reportBuildSize: reportBuildSize,
    addStudioReminder: addStudioReminder,
    openStudio: openStudio,
    matchSpokes: matchSpokes,
    hubVariantAllowed: hubVariantAllowed,
    withFreehubMap: withFreehubMap,
    availableFreehubsForSpacing: availableFreehubsForSpacing,
    syncFreehubButtons: syncFreehubButtons,
    afterAutoSelect: afterAutoSelect,
    requestFeedback: requestFeedback,
    // for tests / console
    _studioConfig: studioConfig, _countsFor: countsFor, _colorText: colorText, _spokeProduct: spokeProduct, _sortedColors: sortedColors, _lineVariantId: lineVariantId, _syncSpokeComponent: syncSpokeComponent, _hubColors: hubColors,
    _rimHoleCount: rimHoleCount, _rimVariantIsTwoToOne: rimVariantIsTwoToOne, _freehubListOf: freehubListOf, _applyBaseChange: applyBaseChange
  };
})(window);
